/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ZonalMindMap.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import PropTypes from "prop-types";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ReactFlow,
  Controls,
  Background,
  ControlButton,
  applyEdgeChanges,
  applyNodeChanges,
  addEdge,
  Handle,
  Position,
  BaseEdge,
  useInternalNode,
  useNodes,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { HandleApiActions, labCar } from "../../redux/actions";
import customConsole from "../../config/customConsole";

const CLAMPED_NODE_LABEL_STYLE = {
  fontSize: "11px",
  fontWeight: "700",
  textAlign: "center",
  lineHeight: "12px",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
};

const calculateClampedPosition = (pos, element) => {
  if (!pos) return { x: 0, y: 0 };
  const margin = 16;
  const viewportWidth =
    typeof window !== "undefined" ? window.innerWidth : 1920;
  const viewportHeight =
    typeof window !== "undefined" ? window.innerHeight : 1080;

  const width = element?.offsetWidth || 380;
  const height = element?.offsetHeight || 260;

  let x = pos.x + 15;
  let y = pos.y - 10;

  // Horizontal boundary check: flip to left of cursor if it overflows right
  if (x + width > viewportWidth - margin) {
    const leftOfCursor = pos.x - width - 15;
    if (leftOfCursor >= margin) {
      x = leftOfCursor;
    } else {
      x = Math.max(margin, viewportWidth - width - margin);
    }
  }
  if (x < margin) x = margin;

  // Vertical boundary check: flip to above cursor or shift up if it overflows bottom
  if (y + height > viewportHeight - margin) {
    const aboveCursor = pos.y - height - 10;
    if (aboveCursor >= margin) {
      y = aboveCursor;
    } else {
      y = Math.max(margin, viewportHeight - height - margin);
    }
  }
  if (y < margin) y = margin;

  return { x, y };
};

const SWMHoverTooltip = ({ open, swmDetails, position, isLinkedSWM }) => {
  const tooltipRef = useRef(null);
  const [adjustedPos, setAdjustedPos] = useState({
    x: position?.x || 0,
    y: position?.y || 0,
  });

  useEffect(() => {
    if (open && position) {
      const calculated = calculateClampedPosition(position, tooltipRef.current);
      setAdjustedPos(calculated);
    }
  }, [open, position?.x, position?.y]);

  if (!open || !swmDetails) return null;

  return (
    <Paper
      ref={tooltipRef}
      elevation={8}
      sx={{
        position: "fixed",
        top: adjustedPos.y,
        left: adjustedPos.x,
        maxWidth: "400px",
        minWidth: "300px",
        maxHeight: "calc(100vh - 32px)",
        overflowY: "auto",
        zIndex: 9999,
        pointerEvents: "none", // Prevent interfering with mouse events
        border: "1px solid #ccc",
        borderRadius: "5px",
        overflow: "hidden",
        boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.25)",
      }}
    >
      <Box
        sx={{
          backgroundColor: isLinkedSWM ? "#C4D600" : "#000000",
          color: isLinkedSWM ? "black" : "white",
          padding: "12px 16px",
          fontSize: "14px",
          fontWeight: "bold",
        }}
      >
        {isLinkedSWM ? "Linked Software Module Details" : "Software Module Details"}
      </Box>

      <Box
        sx={{ textAlign: "left", padding: "16px", backgroundColor: "white" }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            marginBottom: "8px",
          }}
        >
          <strong>Name:</strong> {swmDetails?.name}
        </Typography>

        {swmDetails?.version && (
          <Box sx={{ marginBottom: "8px" }}>
            <Typography
              variant="h6"
              sx={{ fontSize: "14px", fontWeight: "bold", marginBottom: "8px" }}
            >
              <strong>Version:</strong> {swmDetails.version}
            </Typography>
          </Box>
        )}

        {swmDetails?.description && (
          <Box sx={{ marginBottom: "8px" }}>
            <Typography
              variant="h6"
              sx={{ fontSize: "14px", fontWeight: "bold", marginBottom: "8px" }}
            >
              <strong>Description:</strong> {swmDetails?.description}
            </Typography>
          </Box>
        )}

        {isLinkedSWM && (swmDetails?.network_interfaces?.length > 0 || swmDetails?.network_interface) && (
          <>
            <Divider sx={{ marginY: "8px" }} />
            {(swmDetails?.network_interfaces?.length > 0
              ? swmDetails.network_interfaces
              : [swmDetails.network_interface]
            ).map((ni, idx) => (
              <Box
                key={ni?.interface_name || idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  marginBottom: "8px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Network Interface: {ni?.interface_name}
                </Typography>
                {ni?.color && (
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: "0px",
                      backgroundColor: ni.color,
                      border: "1px solid rgba(0, 0, 0, 0.2)",
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
            ))}
          </>
        )}
      </Box>
    </Paper>
  );
};

const EEHoverTooltip = ({ open, eeDetails, position, isLinkedEE }) => {
  const tooltipRef = useRef(null);
  const [adjustedPos, setAdjustedPos] = useState({
    x: position?.x || 0,
    y: position?.y || 0,
  });

  useEffect(() => {
    if (open && position) {
      const calculated = calculateClampedPosition(position, tooltipRef.current);
      setAdjustedPos(calculated);
    }
  }, [open, position?.x, position?.y]);

  if (!open || !eeDetails) return null;

  return (
    <Paper
      ref={tooltipRef}
      elevation={8}
      sx={{
        position: "fixed",
        top: adjustedPos.y,
        left: adjustedPos.x,
        maxWidth: "400px",
        minWidth: "300px",
        maxHeight: "calc(100vh - 32px)",
        overflowY: "auto",
        zIndex: 9999,
        pointerEvents: "none", // Prevent interfering with mouse events
        border: "1px solid #ccc",
        borderRadius: "5px",
        overflow: "hidden",
        boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.25)",
      }}
    >
      <Box
        sx={{
          backgroundColor: isLinkedEE ? "#C4D600" : "#000000",
          color: isLinkedEE ? "black" : "white",
          padding: "12px 16px",
          fontSize: "14px",
          fontWeight: "bold",
        }}
      >
        {isLinkedEE ? "Linked EE Component Details" : "EE Component Details"}
      </Box>

      <Box
        sx={{ textAlign: "left", padding: "16px", backgroundColor: "white" }}
      >
        {eeDetails?.assembly_number && (
          <Box sx={{ marginBottom: "8px" }}>
            <Typography
              variant="h6"
              sx={{ fontSize: "14px", fontWeight: "bold", marginBottom: "8px" }}
            >
              <strong>Assembly Number:</strong> {eeDetails?.assembly_number}
            </Typography>
          </Box>
        )}

        {eeDetails?.version && (
          <Box sx={{ marginBottom: "8px" }}>
            <Typography
              variant="h6"
              sx={{ fontSize: "14px", fontWeight: "bold", marginBottom: "8px" }}
            >
              <strong>Version:</strong> {eeDetails?.version}
            </Typography>
          </Box>
        )}

        <Typography
          variant="h6"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            marginBottom: "8px",
          }}
        >
          <strong>Description:</strong> {eeDetails?.description}
        </Typography>

        {isLinkedEE && (eeDetails?.network_interfaces?.length > 0 || eeDetails?.network_interface) && (
          <>
            <Divider sx={{ marginY: "8px" }} />
            {(eeDetails?.network_interfaces?.length > 0
              ? eeDetails.network_interfaces
              : [eeDetails.network_interface]
            ).map((ni, idx) => (
              <Box
                key={ni?.interface_name || idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  marginBottom: "8px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  Network Interface: {ni?.interface_name}
                </Typography>
                {ni?.color && (
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: "0px",
                      backgroundColor: ni.color,
                      border: "1px solid rgba(0, 0, 0, 0.2)",
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
            ))}
          </>
        )}
      </Box>
    </Paper>
  );
};

const ProjectNode = ({ data }) => {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "6px",
        boxSizing: "border-box",
        overflow: "hidden",
        background: "black",
        color: "white",
        borderRadius: "50%",
        position: "relative",
      }}
    >
      <Handle
        type="source"
        position={Position.Top}
        id="source-top"
        style={{ top: "0%", left: "50%", transform: "translateX(-50%)" }}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="source-top-left"
        style={{ top: "12%", left: "24%", transform: "translate(-50%, -50%)" }}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="source-top-right"
        style={{ top: "12%", left: "76%", transform: "translate(-50%, -50%)" }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="source-bottom"
        style={{ bottom: "0%", left: "50%", transform: "translateX(-50%)" }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="source-bottom-left"
        style={{
          bottom: "12%",
          left: "24%",
          transform: "translate(-50%, 50%)",
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="source-bottom-right"
        style={{
          bottom: "12%",
          left: "76%",
          transform: "translate(-50%, 50%)",
        }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="source-left"
        style={{ left: "0%", top: "50%", transform: "translateY(-50%)" }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="source-right"
        style={{ right: "0%", top: "50%", transform: "translateY(-50%)" }}
      />

      <div style={CLAMPED_NODE_LABEL_STYLE}>{data.name}</div>
    </div>
  );
};

const buildZoneLabelLines = (label, maxLines, maxChars) => {
  const words = (label || "").toString().trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [""];

  const lines = [];
  let line = "";
  let overflow = false;

  const clampWord = (word) => {
    if (word.length <= maxChars) return { text: word, truncated: false };
    const cut = Math.max(1, maxChars - 3);
    return { text: `${word.slice(0, cut)}...`, truncated: true };
  };

  for (const word of words) {
    const { text: wordText, truncated } = clampWord(word);
    overflow = overflow || truncated;

    if (!line) {
      line = wordText;
      continue;
    }

    if (`${line} ${wordText}`.length <= maxChars) {
      line = `${line} ${wordText}`;
      continue;
    }

    lines.push(line);
    if (lines.length === maxLines) {
      overflow = true;
      line = "";
      break;
    }
    line = wordText;
  }

  if (line) lines.push(line);

  if (lines.length > maxLines) {
    lines.length = maxLines;
    overflow = true;
  }

  if (overflow) {
    const lastIndex = lines.length - 1;
    const lastLine = lines[lastIndex] || "";
    if (!lastLine.endsWith("...")) {
      const cut = Math.max(1, maxChars - 3);
      lines[lastIndex] = `${lastLine.slice(0, cut)}...`;
    }
  }

  return lines;
};

const ZoneNode = ({ data }) => {
  const label = (data?.labelText || "").toString();
  const lines = buildZoneLabelLines(label, 2, 10);
  const rotate = !!data?.rotate;
  const textY = rotate ? "45%" : "70%";
  const isFromFrontSide = !!data?.isFromFrontSide;
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "visible",
      }}
    >
      {isFromFrontSide &&
        (data?.labelText || "").toString().trim().toUpperCase() !== "FRONT" && (
          <Handle
            type="target"
            position={Position.Top}
          />
        )}

      {(data?.labelText || "").toString().trim().toUpperCase() === "REAR" && (
        <Handle
          type="target"
          position={Position.Top}
          id="zone-top-target"
          style={{
            left: "50%",
            transform: "translateX(-50%)",
          }}
        />
      )}

      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: "100%", height: "100%", overflow: "visible" }}
      >
        <polygon
          points="50,0 100,100 0,100"
          fill="#2E8B46"
          stroke="#0e5e22"
          strokeWidth="2"
          transform={rotate ? "rotate(180 50 50)" : undefined}
        />
        <text
          x="50%"
          y={textY}
          fill="white"
          fontWeight="700"
          fontSize="11"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {lines.map((lineText, i) => (
            <tspan key={`${lineText}-${i}`} x="50%" dy={i === 0 ? "-6" : "13"}>
              {lineText}
            </tspan>
          ))}
        </text>
      </svg>
      {(!isFromFrontSide ||
        (data?.labelText || "").toString().trim().toUpperCase() ===
        "FRONT") && (
          <Handle
            type="target"
            position={Position.Bottom}
            id="zone-bottom-target"
            style={{
              left: "50%",
              transform: "translateX(-50%)",
            }}
          />
        )}
      {(() => {
        const labelText = (data?.labelText || "").toString().trim().toUpperCase();
        if (labelText === "FRONT") {
          return (
            <Handle
              type="source"
              position={Position.Top}
              id="zone-top-source"
              style={{ left: "50%", transform: "translateX(-50%)" }}
            />
          );
        }
        if (labelText === "REAR") {
          return (
            <Handle
              type="source"
              position={Position.Bottom}
              id="zone-top-source"
              style={{ left: "50%", transform: "translateX(-50%)" }}
            />
          );
        }
        return (
          <Handle
            type="source"
            position={isFromFrontSide ? Position.Bottom : Position.Top}
            id={isFromFrontSide ? "zone-bottom-source" : "zone-top-source"}
            style={{ left: "50%", transform: "translateX(-50%)" }}
          />
        );
      })()}
    </div>
  );
};

const SWMNode = ({ data }) => {
  const isRearZone = data?.isRearZone || false;
  const isFrontZone = data?.isFrontZone || false;
  const isCentralLeftZone = data?.isCentralLeftZone || false;
  const isRearLeftZone = data?.isRearLeftZone || false;
  const isRearRightZone = data?.isRearRightZone || false;

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "4px",
        boxSizing: "border-box",
        background: data.background || "#2897DC",
        color: "black",
        cursor: "pointer",
        transition: "all 0.2s ease",
        border: "1px solid rgba(255,255,255,0.3)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {(() => {
        if (isRearZone) {
          return (
            <>
              <Handle
                type="target"
                position={Position.Left}
                style={{ left: "0%", top: "50%", transform: "translateY(-50%)" }}
              />
              <Handle
                type="source"
                position={Position.Left}
                style={{ left: "0%", top: "50%", transform: "translateY(-50%)" }}
              />
            </>
          );
        }
        if (isFrontZone) {
          return (
            <>
              <Handle
                type="target"
                position={Position.Right}
                style={{ right: "0%", top: "50%", transform: "translateY(-50%)" }}
              />
              <Handle
                type="source"
                position={Position.Right}
                style={{ right: "0%", top: "50%", transform: "translateY(-50%)" }}
              />
            </>
          );
        }
        if (isCentralLeftZone || isRearLeftZone || isRearRightZone) {
          return (
            <>
              <Handle
                type="target"
                position={Position.Top}
                style={{ top: "0%", left: "50%", transform: "translateX(-50%)" }}
              />
              <Handle
                type="source"
                position={Position.Top}
                style={{ top: "0%", left: "50%", transform: "translateX(-50%)" }}
              />
            </>
          );
        }
        return (
          <>
            <Handle
              type="target"
              position={Position.Bottom}
              style={{ bottom: "0%", left: "50%", transform: "translateX(-50%)" }}
            />
            <Handle
              type="source"
              position={Position.Bottom}
              style={{ bottom: "0%", left: "50%", transform: "translateX(-50%)" }}
            />
          </>
        );
      })()}

      <h3
        style={{
          margin: 0,
          fontSize: "10px",
          fontWeight: "bold",
          textAlign: "center",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          lineHeight: "12px",
          maxHeight: "24px",
        }}
      >
        {data.name}
      </h3>
    </div>
  );
};

const segmentHitsBox = (p1, p2, box, padding = 4) => {
  const bMinX = box.x - padding;
  const bMaxX = box.x + box.w + padding;
  const bMinY = box.y - padding;
  const bMaxY = box.y + box.h + padding;

  if (Math.abs(p1.y - p2.y) < 1) {
    const y = p1.y;
    if (y < bMinY || y > bMaxY) return false;
    const segMinX = Math.min(p1.x, p2.x);
    const segMaxX = Math.max(p1.x, p2.x);
    return segMaxX > bMinX && segMinX < bMaxX;
  }

  if (Math.abs(p1.x - p2.x) < 1) {
    const x = p1.x;
    if (x < bMinX || x > bMaxX) return false;
    const segMinY = Math.min(p1.y, p2.y);
    const segMaxY = Math.max(p1.y, p2.y);
    return segMaxY > bMinY && segMinY < bMaxY;
  }

  const minX = Math.min(p1.x, p2.x);
  const maxX = Math.max(p1.x, p2.x);
  const minY = Math.min(p1.y, p2.y);
  const maxY = Math.max(p1.y, p2.y);
  if (maxX < bMinX || minX > bMaxX || maxY < bMinY || minY > bMaxY) return false;
  return true;
};

const segmentPenetratesBox = (p1, p2, box, padding = 2) => {
  const bMinX = box.x + padding;
  const bMaxX = box.x + box.w - padding;
  const bMinY = box.y + padding;
  const bMaxY = box.y + box.h - padding;

  if (bMaxX <= bMinX || bMaxY <= bMinY) return false;

  if (Math.abs(p1.y - p2.y) < 1) {
    const y = p1.y;
    if (y < bMinY || y > bMaxY) return false;
    const segMinX = Math.min(p1.x, p2.x);
    const segMaxX = Math.max(p1.x, p2.x);
    return segMaxX > bMinX && segMinX < bMaxX;
  }

  if (Math.abs(p1.x - p2.x) < 1) {
    const x = p1.x;
    if (x < bMinX || x > bMaxX) return false;
    const segMinY = Math.min(p1.y, p2.y);
    const segMaxY = Math.max(p1.y, p2.y);
    return segMaxY > bMinY && segMinY < bMaxY;
  }

  return false;
};

const countCollisions = (points, obstacles, srcBox, tgtBox, padding = 4) => {
  let count = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];

    for (const obs of obstacles) {
      if (segmentHitsBox(p1, p2, obs, padding)) {
        count++;
      }
    }

    if (srcBox && segmentPenetratesBox(p1, p2, srcBox, 2)) {
      count += 10;
    }
    if (tgtBox && segmentPenetratesBox(p1, p2, tgtBox, 2)) {
      count += 10;
    }
  }
  return count;
};

const pathLength = (points) => {
  let len = 0;
  for (let i = 0; i < points.length - 1; i++) {
    len += Math.hypot(points[i + 1].x - points[i].x, points[i + 1].y - points[i].y);
  }
  return len;
};

const cleanPoints = (pts) => {
  if (pts.length < 2) return pts;
  const result = [pts[0]];
  for (let i = 1; i < pts.length; i++) {
    const prev = result[result.length - 1];
    const curr = pts[i];
    if (Math.abs(prev.x - curr.x) < 1 && Math.abs(prev.y - curr.y) < 1) continue;
    result.push(curr);
  }

  const simplified = [result[0]];
  for (let i = 1; i < result.length - 1; i++) {
    const prev = simplified[simplified.length - 1];
    const curr = result[i];
    const next = result[i + 1];

    const dx1 = curr.x - prev.x;
    const dy1 = curr.y - prev.y;
    const dx2 = next.x - curr.x;
    const dy2 = next.y - curr.y;

    if (Math.abs(dy1) < 1 && Math.abs(dy2) < 1) continue;
    if (Math.abs(dx1) < 1 && Math.abs(dx2) < 1) continue;

    simplified.push(curr);
  }
  simplified.push(result[result.length - 1]);
  return simplified;
};

const buildSmoothOrthogonalPath = (points, radius = 16) => {
  const pts = cleanPoints(points);
  if (pts.length < 2) return "";
  if (pts.length === 2) {
    return `M ${pts[0].x} ${pts[0].y} L ${pts[1].x} ${pts[1].y}`;
  }

  let d = `M ${pts[0].x} ${pts[0].y}`;

  for (let i = 1; i < pts.length - 1; i++) {
    const pPrev = pts[i - 1];
    const pCurr = pts[i];
    const pNext = pts[i + 1];

    const d1 = { x: pCurr.x - pPrev.x, y: pCurr.y - pPrev.y };
    const len1 = Math.hypot(d1.x, d1.y);
    const d2 = { x: pNext.x - pCurr.x, y: pNext.y - pCurr.y };
    const len2 = Math.hypot(d2.x, d2.y);

    if (len1 === 0 || len2 === 0) continue;

    const r = Math.min(radius, len1 / 2, len2 / 2);

    const startX = pCurr.x - (d1.x / len1) * r;
    const startY = pCurr.y - (d1.y / len1) * r;
    const endX = pCurr.x + (d2.x / len2) * r;
    const endY = pCurr.y + (d2.y / len2) * r;

    d += ` L ${startX} ${startY} Q ${pCurr.x} ${pCurr.y} ${endX} ${endY}`;
  }

  const pLast = pts[pts.length - 1];
  d += ` L ${pLast.x} ${pLast.y}`;
  return d;
};

/**
 * Calculates an obstacle-avoiding routing edge path that evaluates all candidate
 * paths against intermediate software modules, EE components, and zones, guaranteed
 * to select a path with zero collisions with other software modules.
 */
const getPerimeterEdgePath = (
  sourceNode,
  targetNode,
  allNodes = [],
  linkIndex = 0,
  totalLinks = 1,
) => {
  if (!sourceNode || !targetNode) return "";

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  const obstacles = [];

  for (const n of allNodes) {
    const pos =
      n.internals?.positionAbsolute || n.position || { x: 0, y: 0 };
    const w = n.measured?.width || n.style?.width || 50;
    const h = n.measured?.height || n.style?.height || 50;
    const wNum = typeof w === "number" ? w : 50;
    const hNum = typeof h === "number" ? h : 50;

    minX = Math.min(minX, pos.x);
    maxX = Math.max(maxX, pos.x + wNum);
    minY = Math.min(minY, pos.y);
    maxY = Math.max(maxY, pos.y + hNum);

    if (n.id !== sourceNode.id && n.id !== targetNode.id) {
      obstacles.push({ id: n.id, x: pos.x, y: pos.y, w: wNum, h: hNum });
    }
  }

  if (!Number.isFinite(minX)) {
    minX = -600;
    maxX = 600;
    minY = -600;
    maxY = 600;
  }

  // Dedicated clearance lane outside the entire diagram, compact and staggered per link
  const clearance = 20 + linkIndex * 10;
  const leftLane = minX - clearance;
  const rightLane = maxX + clearance;
  const topLane = minY - clearance;
  const bottomLane = maxY + clearance;

  const wA =
    sourceNode.measured?.width ||
    (typeof sourceNode.style?.width === "number"
      ? sourceNode.style.width
      : 50);
  const hA =
    sourceNode.measured?.height ||
    (typeof sourceNode.style?.height === "number"
      ? sourceNode.style.height
      : 50);
  const wB =
    targetNode.measured?.width ||
    (typeof targetNode.style?.width === "number"
      ? targetNode.style.width
      : 50);
  const hB =
    targetNode.measured?.height ||
    (typeof targetNode.style?.height === "number"
      ? targetNode.style.height
      : 50);

  const posA =
    sourceNode.internals?.positionAbsolute || sourceNode.position || { x: 0, y: 0 };
  const posB =
    targetNode.internals?.positionAbsolute || targetNode.position || { x: 0, y: 0 };

  const cA = { x: posA.x + wA / 2, y: posA.y + hA / 2 };
  const cB = { x: posB.x + wB / 2, y: posB.y + hB / 2 };

  const srcBox = { x: posA.x, y: posA.y, w: wA, h: hA };
  const tgtBox = { x: posB.x, y: posB.y, w: wB, h: hB };

  const maxAnchorSpread = 16;
  const stepSpread = totalLinks > 1 ? maxAnchorSpread / (totalLinks - 1) : 0;
  const anchorOffset =
    totalLinks > 1 ? -maxAnchorSpread / 2 + linkIndex * stepSpread : 0;

  const dx = cB.x - cA.x;
  const dy = cB.y - cA.y;
  const isHorizontalSeparation = Math.abs(dx) >= Math.abs(dy);
  const offset = 16 + linkIndex * 8;

  const rawCandidates = [];

  // 1. Local direction-aware bypasses (clean facing connect or arch around)
  if (isHorizontalSeparation) {
    if (Math.abs(cA.y - cB.y) < 15) {
      if (dx > 0) {
        rawCandidates.push([
          { x: posA.x + wA, y: cA.y + anchorOffset },
          { x: posB.x, y: cA.y + anchorOffset },
        ]);
      } else {
        rawCandidates.push([
          { x: posA.x, y: cA.y + anchorOffset },
          { x: posB.x + wB, y: cA.y + anchorOffset },
        ]);
      }
    }

    if (dx > 0) {
      // Node A is on the left, Node B is on the right
      const midX = posA.x + wA + (posB.x - (posA.x + wA)) / 2;
      rawCandidates.push([
        { x: posA.x + wA, y: cA.y + anchorOffset },
        { x: midX, y: cA.y + anchorOffset },
        { x: midX, y: cB.y + anchorOffset * 0.5 },
        { x: posB.x, y: cB.y + anchorOffset * 0.5 },
      ]);
    } else {
      // Node B is on the left, Node A is on the right
      const midX = posB.x + wB + (posA.x - (posB.x + wB)) / 2;
      rawCandidates.push([
        { x: posA.x, y: cA.y + anchorOffset },
        { x: midX, y: cA.y + anchorOffset },
        { x: midX, y: cB.y + anchorOffset * 0.5 },
        { x: posB.x + wB, y: cB.y + anchorOffset * 0.5 },
      ]);
    }

    // Top arch bypass (above both horizontal nodes)
    const topY = Math.min(posA.y, posB.y) - offset;
    rawCandidates.push([
      { x: cA.x + anchorOffset, y: posA.y },
      { x: cA.x + anchorOffset, y: topY },
      { x: cB.x + anchorOffset * 0.5, y: topY },
      { x: cB.x + anchorOffset * 0.5, y: posB.y },
    ]);

    // Bottom arch bypass (below both horizontal nodes)
    const bottomY = Math.max(posA.y + hA, posB.y + hB) + offset;
    rawCandidates.push([
      { x: cA.x + anchorOffset, y: posA.y + hA },
      { x: cA.x + anchorOffset, y: bottomY },
      { x: cB.x + anchorOffset * 0.5, y: bottomY },
      { x: cB.x + anchorOffset * 0.5, y: posB.y + hB },
    ]);
  } else {
    if (Math.abs(cA.x - cB.x) < 15) {
      if (dy > 0) {
        rawCandidates.push([
          { x: cA.x + anchorOffset, y: posA.y + hA },
          { x: cA.x + anchorOffset, y: posB.y },
        ]);
      } else {
        rawCandidates.push([
          { x: cA.x + anchorOffset, y: posA.y },
          { x: cA.x + anchorOffset, y: posB.y + hB },
        ]);
      }
    }

    if (dy > 0) {
      // Node A is on top, Node B is on bottom
      const midY = posA.y + hA + (posB.y - (posA.y + hA)) / 2;
      rawCandidates.push([
        { x: cA.x + anchorOffset, y: posA.y + hA },
        { x: cA.x + anchorOffset, y: midY },
        { x: cB.x + anchorOffset * 0.5, y: midY },
        { x: cB.x + anchorOffset * 0.5, y: posB.y },
      ]);
    } else {
      // Node B is on top, Node A is on bottom
      const midY = posB.y + hB + (posA.y - (posB.y + hB)) / 2;
      rawCandidates.push([
        { x: cA.x + anchorOffset, y: posA.y },
        { x: cA.x + anchorOffset, y: midY },
        { x: cB.x + anchorOffset * 0.5, y: midY },
        { x: cB.x + anchorOffset * 0.5, y: posB.y + hB },
      ]);
    }

    // Right bypass (to the right of both vertical nodes)
    const rightX = Math.max(posA.x + wA, posB.x + wB) + offset;
    rawCandidates.push([
      { x: posA.x + wA, y: cA.y + anchorOffset },
      { x: rightX, y: cA.y + anchorOffset },
      { x: rightX, y: cB.y + anchorOffset * 0.5 },
      { x: posB.x + wB, y: cB.y + anchorOffset * 0.5 },
    ]);

    // Left bypass (to the left of both vertical nodes)
    const leftX = Math.min(posA.x, posB.x) - offset;
    rawCandidates.push([
      { x: posA.x, y: cA.y + anchorOffset },
      { x: leftX, y: cA.y + anchorOffset },
      { x: leftX, y: cB.y + anchorOffset * 0.5 },
      { x: posB.x, y: cB.y + anchorOffset * 0.5 },
    ]);
  }

  // 2. Perimeter clearance paths (routes around entire diagram outside all nodes)
  const srcPorts = [
    {
      side: "right",
      start: { x: posA.x + wA, y: cA.y + anchorOffset },
      lane: { x: rightLane, y: cA.y + anchorOffset },
    },
    {
      side: "left",
      start: { x: posA.x, y: cA.y + anchorOffset },
      lane: { x: leftLane, y: cA.y + anchorOffset },
    },
    {
      side: "top",
      start: { x: cA.x + anchorOffset, y: posA.y },
      lane: { x: cA.x + anchorOffset, y: topLane },
    },
    {
      side: "bottom",
      start: { x: cA.x + anchorOffset, y: posA.y + hA },
      lane: { x: cA.x + anchorOffset, y: bottomLane },
    },
  ];

  const tgtPorts = [
    {
      side: "right",
      start: { x: posB.x + wB, y: cB.y + anchorOffset * 0.5 },
      lane: { x: rightLane, y: cB.y + anchorOffset * 0.5 },
    },
    {
      side: "left",
      start: { x: posB.x, y: cB.y + anchorOffset * 0.5 },
      lane: { x: leftLane, y: cB.y + anchorOffset * 0.5 },
    },
    {
      side: "top",
      start: { x: cB.x + anchorOffset * 0.5, y: posB.y },
      lane: { x: cB.x + anchorOffset * 0.5, y: topLane },
    },
    {
      side: "bottom",
      start: { x: cB.x + anchorOffset * 0.5, y: posB.y + hB },
      lane: { x: cB.x + anchorOffset * 0.5, y: bottomLane },
    },
  ];

  const corners = {
    topLeft: { x: leftLane, y: topLane },
    topRight: { x: rightLane, y: topLane },
    bottomLeft: { x: leftLane, y: bottomLane },
    bottomRight: { x: rightLane, y: bottomLane },
  };

  for (const sPort of srcPorts) {
    for (const tPort of tgtPorts) {
      if (sPort.side === tPort.side) {
        rawCandidates.push([sPort.start, sPort.lane, tPort.lane, tPort.start]);
      } else if (
        (sPort.side === "left" && tPort.side === "top") ||
        (sPort.side === "top" && tPort.side === "left")
      ) {
        rawCandidates.push([sPort.start, sPort.lane, corners.topLeft, tPort.lane, tPort.start]);
      } else if (
        (sPort.side === "right" && tPort.side === "top") ||
        (sPort.side === "top" && tPort.side === "right")
      ) {
        rawCandidates.push([sPort.start, sPort.lane, corners.topRight, tPort.lane, tPort.start]);
      } else if (
        (sPort.side === "left" && tPort.side === "bottom") ||
        (sPort.side === "bottom" && tPort.side === "left")
      ) {
        rawCandidates.push([sPort.start, sPort.lane, corners.bottomLeft, tPort.lane, tPort.start]);
      } else if (
        (sPort.side === "right" && tPort.side === "bottom") ||
        (sPort.side === "bottom" && tPort.side === "right")
      ) {
        rawCandidates.push([sPort.start, sPort.lane, corners.bottomRight, tPort.lane, tPort.start]);
      } else if (sPort.side === "left" && tPort.side === "right") {
        rawCandidates.push([sPort.start, sPort.lane, corners.topLeft, corners.topRight, tPort.lane, tPort.start]);
        rawCandidates.push([sPort.start, sPort.lane, corners.bottomLeft, corners.bottomRight, tPort.lane, tPort.start]);
      } else if (sPort.side === "right" && tPort.side === "left") {
        rawCandidates.push([sPort.start, sPort.lane, corners.topRight, corners.topLeft, tPort.lane, tPort.start]);
        rawCandidates.push([sPort.start, sPort.lane, corners.bottomRight, corners.bottomLeft, tPort.lane, tPort.start]);
      } else if (sPort.side === "top" && tPort.side === "bottom") {
        rawCandidates.push([sPort.start, sPort.lane, corners.topLeft, corners.bottomLeft, tPort.lane, tPort.start]);
        rawCandidates.push([sPort.start, sPort.lane, corners.topRight, corners.bottomRight, tPort.lane, tPort.start]);
      } else if (sPort.side === "bottom" && tPort.side === "top") {
        rawCandidates.push([sPort.start, sPort.lane, corners.bottomLeft, corners.topLeft, tPort.lane, tPort.start]);
        rawCandidates.push([sPort.start, sPort.lane, corners.bottomRight, corners.topRight, tPort.lane, tPort.start]);
      }
    }
  }

  // Filter and select path with zero collisions and minimum length
  let bestPath = null;
  let minCol = Infinity;
  let minLen = Infinity;

  for (const raw of rawCandidates) {
    const pts = cleanPoints(raw);
    const col = countCollisions(pts, obstacles, srcBox, tgtBox);
    const len = pathLength(pts);

    if (col < minCol) {
      minCol = col;
      minLen = len;
      bestPath = pts;
    } else if (col === minCol && len < minLen) {
      minLen = len;
      bestPath = pts;
    }
  }

  return bestPath ? buildSmoothOrthogonalPath(bestPath, 16) : "";
};

const SwmLinkEdge = ({ id, source, target, style, markerEnd, data }) => {
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  const allNodes = useNodes();
  const linkIndex = data?.linkIndex || 0;
  const totalLinks = data?.totalLinks || 1;

  const edgePath = getPerimeterEdgePath(
    sourceNode,
    targetNode,
    allNodes,
    linkIndex,
    totalLinks,
  );

  if (!edgePath) return null;

  const strokeWidth = Number(style?.strokeWidth) || 3.5;

  return (
    <g className="react-flow__edge-swm-link" style={{ pointerEvents: "none" }}>
      {/* Halo / casing underlay: acts as a clean visual bridge over crossing lines */}
      <path
        d={edgePath}
        fill="none"
        stroke="#ffffff"
        strokeWidth={strokeWidth + 4}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          strokeDasharray: "none",
          animation: "none",
          opacity: 0.95,
        }}
      />
      {/* Active colored edge path */}
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          ...style,
          strokeWidth,
        }}
        markerEnd={markerEnd}
      />
    </g>
  );
};

SwmLinkEdge.propTypes = {
  id: PropTypes.string,
  source: PropTypes.string,
  target: PropTypes.string,
  style: PropTypes.object,
  markerEnd: PropTypes.any,
  data: PropTypes.shape({
    linkIndex: PropTypes.number,
    totalLinks: PropTypes.number,
    color: PropTypes.string,
  }),
};

const NODE_TYPES = {
  zoneNode: ZoneNode,
  projectNode: ProjectNode,
  swmNode: SWMNode,
};

const EDGE_TYPES = {
  swmLinkEdge: SwmLinkEdge,
};

const nodeWidth = 140;
const nodeHeight = 140;

const ZONE_DIRS = {
  TOP: { x: 0, y: -1 },
  FRONT_LEFT: { x: -0.7, y: -0.9 },
  FRONT_RIGHT: { x: 0.7, y: -0.9 },
  CENTER_LEFT: { x: -1.4, y: 0 },
  CENTER_RIGHT: { x: 1.4, y: 0 },
  BOTTOM_LEFT: { x: -0.7, y: 0.9 },
  BOTTOM_RIGHT: { x: 0.7, y: 0.9 },
  REAR: { x: 0, y: 1.1 },
  TOP_LEFT: { x: -1, y: -1 },
  TOP_RIGHT: { x: 1, y: -1 },
  BOTTOM: { x: 0, y: 1 },
};

const PER_ZONE_ANCHORS = {
  "FRONT LEFT": [
    ZONE_DIRS.TOP,
    ZONE_DIRS.FRONT_LEFT,
    ZONE_DIRS.CENTER_LEFT,
    ZONE_DIRS.BOTTOM_LEFT,
  ],
  "FRONT RIGHT": [
    ZONE_DIRS.TOP,
    ZONE_DIRS.FRONT_RIGHT,
    ZONE_DIRS.CENTER_RIGHT,
    ZONE_DIRS.BOTTOM_RIGHT,
  ],
  "REAR LEFT": [ZONE_DIRS.CENTER_LEFT, ZONE_DIRS.BOTTOM_LEFT, ZONE_DIRS.REAR],
  "REAR RIGHT": [
    ZONE_DIRS.CENTER_RIGHT,
    ZONE_DIRS.BOTTOM_RIGHT,
    ZONE_DIRS.REAR,
  ],
  "CENTRAL LEFT": [
    ZONE_DIRS.TOP_LEFT,
    ZONE_DIRS.CENTER_LEFT,
    ZONE_DIRS.BOTTOM_LEFT,
  ],
  "CENTRAL RIGHT": [
    ZONE_DIRS.TOP_RIGHT,
    ZONE_DIRS.CENTER_RIGHT,
    ZONE_DIRS.BOTTOM_RIGHT,
  ],
  FRONT: [
    ZONE_DIRS.TOP,
    ZONE_DIRS.FRONT_LEFT,
    ZONE_DIRS.FRONT_RIGHT,
    ZONE_DIRS.CENTER_LEFT,
    ZONE_DIRS.CENTER_RIGHT,
  ],
  REAR: [
    ZONE_DIRS.REAR,
    ZONE_DIRS.BOTTOM_LEFT,
    ZONE_DIRS.BOTTOM_RIGHT,
    ZONE_DIRS.CENTER_LEFT,
    ZONE_DIRS.CENTER_RIGHT,
  ],
};

const DEFAULT_ANCHOR_DIRS = [
  ZONE_DIRS.TOP_LEFT,
  ZONE_DIRS.TOP_RIGHT,
  ZONE_DIRS.BOTTOM_LEFT,
  ZONE_DIRS.BOTTOM_RIGHT,
  ZONE_DIRS.CENTER_LEFT,
  ZONE_DIRS.CENTER_RIGHT,
];

const getSwmId = (nodeId) => Number(String(nodeId || "").replaceAll("swm-", ""));
const getEeId = (nodeId) => Number(String(nodeId || "").replaceAll("ee-", ""));

const getNodeColor = (nodeId, selectedNodeId, linkedSwmIds) => {
  customConsole("nodeId, selectedNodeId, linkedSwmIds");
  customConsole({ nodeId, selectedNodeId, linkedSwmIds });
  if (nodeId === selectedNodeId) return "#A0DCFF";
  if (
    selectedNodeId &&
    linkedSwmIds?.includes(Number(selectedNodeId?.replaceAll("swm-", "")))
  )
    return "#C4D600";
  return "#62B5E5";
};

const getZoneNameKey = (zone) =>
  (zone?.name || "").toString().trim().toUpperCase();

const findZoneByName = (zones, name) =>
  (zones || []).find((z) => getZoneNameKey(z) === name);

const getMaxExpandedSwms = (zone, expandedNodes) => {
  if (!zone || !expandedNodes?.has(zone.id)) return 0;
  const ees = zone.children || [];
  return Math.max(
    ...ees.map((ee) =>
      expandedNodes.has(ee.id) ? (ee.children || []).length : 0,
    ),
    0,
  );
};

const getSwmExtension = (maxSwms, extraSpacing, width = nodeWidth) => {
  if (maxSwms <= 0) return 0;
  const baseEeSize = Math.min(width, 140);
  const swmSize = Math.max(36, Math.floor(baseEeSize * 0.55));
  const swmSpacing = swmSize + 12;
  return maxSwms * swmSpacing + extraSpacing + baseEeSize;
};

const getZoneOverlapOffset = ({
  firstZoneName,
  secondZoneName,
  axis,
  direction,
  zones,
  expandedNodes,
  nameToPos,
  minSafeDistance = 180,
}) => {
  const firstZone = findZoneByName(zones, firstZoneName);
  const secondZone = findZoneByName(zones, secondZoneName);
  const maxSwms = getMaxExpandedSwms(firstZone, expandedNodes);

  if (!firstZone || !secondZone || maxSwms <= 0) return 0;

  const ext = getSwmExtension(maxSwms, 30);
  if (direction === "forward") {
    return Math.max(
      0,
      nameToPos[firstZoneName][axis] + ext + minSafeDistance - nameToPos[secondZoneName][axis],
    );
  }
  return Math.max(
    0,
    nameToPos[secondZoneName][axis] - (nameToPos[firstZoneName][axis] - ext - minSafeDistance),
  );
};

const calculateZoneOverlapOffsets = ({ zones, expandedNodes, nameToPos }) => ({
  rearLeftYOffset: getZoneOverlapOffset({
    firstZoneName: "CENTRAL LEFT",
    secondZoneName: "REAR LEFT",
    axis: "y",
    direction: "forward",
    zones,
    expandedNodes,
    nameToPos,
  }),
  frontRightYOffset: getZoneOverlapOffset({
    firstZoneName: "CENTRAL RIGHT",
    secondZoneName: "FRONT RIGHT",
    axis: "y",
    direction: "backward",
    zones,
    expandedNodes,
    nameToPos,
  }),
  rearRightXOffset: getZoneOverlapOffset({
    firstZoneName: "REAR",
    secondZoneName: "REAR RIGHT",
    axis: "x",
    direction: "forward",
    zones,
    expandedNodes,
    nameToPos,
  }),
  frontLeftXOffset: getZoneOverlapOffset({
    firstZoneName: "FRONT",
    secondZoneName: "FRONT LEFT",
    axis: "x",
    direction: "backward",
    zones,
    expandedNodes,
    nameToPos,
  }),
});

const isFrontZone = (zoneKeyUpper) =>
  zoneKeyUpper === "FRONT LEFT" ||
  zoneKeyUpper === "FRONT RIGHT" ||
  zoneKeyUpper === "FRONT";

const isBottomFlowZone = (zoneKeyUpper) =>
  zoneKeyUpper === "CENTRAL LEFT" ||
  zoneKeyUpper === "REAR LEFT" ||
  zoneKeyUpper === "REAR RIGHT";

const getEeSourceConfig = (zoneKeyUpper) => {
  if (zoneKeyUpper === "FRONT" || zoneKeyUpper === "REAR") {
    return {
      sourceHandle: "zone-top-source",
      sourcePosition: Position.Top,
    };
  }

  return {
    sourceHandle: undefined,
    sourcePosition: Position.Top,
  };
};

const shouldRenderSwm = ({
  swm,
  selectedNodeId,
  swmLinkedMap,
  swmParentEeMap,
}) => {
  if (!selectedNodeId?.startsWith("swm-")) return true;

  const selSwmNum = Number(selectedNodeId.replaceAll("swm-", ""));
  const currentSwmNum = Number(String(swm.id || "").replaceAll("swm-", ""));

  const linkedToSelected = (swmLinkedMap.get(selSwmNum) || []).includes(
    currentSwmNum,
  );

  const isSame = selSwmNum === currentSwmNum;

  const sameParent =
    swmParentEeMap.get(selSwmNum) === swmParentEeMap.get(currentSwmNum);

  return isSame || linkedToSelected || sameParent;
};

const getHorizontalNudge = (zoneKeyUpper) => {
  const map = {
    "REAR LEFT": 28,
    "REAR RIGHT": 28,
    "FRONT RIGHT": 31,
    "FRONT LEFT": 30,
    "CENTRAL RIGHT": 33,
    "CENTRAL LEFT": 32,
  };
  return map[zoneKeyUpper] ?? 27;
};

const getSwmPosition = ({
  isRightPlacement,
  isLeftPlacement,
  swmBaseX,
  swmSpacing,
  sidx,
  y,
  zoneKeyUpper,
  x,
  isTopPlacement,
  swmBaseY,
}) => {
  if (isRightPlacement) {
    return { sx: swmBaseX + sidx * swmSpacing, sy: y + 30 };
  }

  if (isLeftPlacement) {
    return { sx: swmBaseX - sidx * swmSpacing, sy: y + 30 };
  }

  const sx = x + getHorizontalNudge(zoneKeyUpper);
  const sy = isTopPlacement
    ? swmBaseY - sidx * swmSpacing
    : swmBaseY + sidx * swmSpacing;

  return { sx, sy };
};

const getSwmEdgePositions = (zoneKeyUpper) => ({
  sourcePosition: isBottomFlowZone(zoneKeyUpper)
    ? Position.Bottom
    : undefined,
  targetPosition: isBottomFlowZone(zoneKeyUpper) ? Position.Top : undefined,
});

const renderSwms = ({
  ee,
  nodes,
  edges,
  zoneKeyUpper,
  selectedNodeId,
  swmLinkedMap,
  swmParentEeMap,
  x,
  y,
  currentEeSize,
  filterMatches,
  focusedFilter,
}) => {
  const rawSwms = ee.children || [];
  const visibleSwms = filterMatches?.isFilterActive
    ? (filterMatches.filterSwm
        ? rawSwms.filter((swm) => {
            const swmId = getSwmId(swm.id);
            if (focusedFilter) {
              return focusedFilter.type === "swm" && focusedFilter.visibleSwmIds.has(swmId);
            }
            return filterMatches.matchingSwmIds.has(swmId);
          })
        : [])
    : rawSwms;

  if (visibleSwms.length === 0) return;
  const swmSize = Math.max(36, Math.floor(currentEeSize * 0.55));
  const swmSpacing = swmSize + 12;

  const isTopPlacement =
    zoneKeyUpper === "FRONT LEFT" ||
    zoneKeyUpper === "FRONT RIGHT" ||
    zoneKeyUpper === "CENTRAL RIGHT";

  const isRightPlacement = zoneKeyUpper === "REAR";
  const isLeftPlacement = zoneKeyUpper === "FRONT";

  const getExtraDownForRear = () => {
    if (zoneKeyUpper === "REAR LEFT" || zoneKeyUpper === "REAR RIGHT") return swmSpacing * 1.8;
    if (zoneKeyUpper === "CENTRAL LEFT") return 50;
    return 35;
  };
  const extraDownForRear = getExtraDownForRear();

  const swmBaseY = isTopPlacement
    ? y - currentEeSize / 2 - swmSpacing - 15
    : y + currentEeSize / 2 + extraDownForRear + swmSize / 2 + 15;

  const getSwmBaseX = () => {
    if (isRightPlacement) return x + currentEeSize / 2 + swmSpacing + 15;
    if (isLeftPlacement) return x - currentEeSize / 2 - swmSpacing - 15;
    return x;
  };
  const swmBaseX = getSwmBaseX();

  for (const [sidx, swm] of visibleSwms.entries()) {
    if (
      !filterMatches?.isFilterActive &&
      !shouldRenderSwm({
        swm,
        selectedNodeId,
        swmLinkedMap,
        swmParentEeMap,
      })
    )
      continue;

    const { sx, sy } = getSwmPosition({
      isRightPlacement,
      isLeftPlacement,
      swmBaseX,
      swmSpacing,
      sidx,
      y,
      zoneKeyUpper,
      x,
      isTopPlacement,
      swmBaseY,
    });

    const swmIdNum = getSwmId(swm.id);
    const isFilterMatchedSwm =
      filterMatches?.isFilterActive &&
      filterMatches?.filterSwm &&
      (focusedFilter
        ? focusedFilter.visibleSwmIds.has(swmIdNum)
        : filterMatches.matchingSwmIds?.has(swmIdNum));

    const isSwmSelected = swm.id === selectedNodeId;
    let swmBg = getNodeColor(swm.id, selectedNodeId, swm.linkedSwmIds);
    if (isFilterMatchedSwm) {
      swmBg = "#C4D600";
    } else if (isSwmSelected) {
      swmBg = "#A0DCFF";
    }

    nodes.push({
      id: swm.id,
      type: "swmNode",
      data: {
        name: swm.name,
        isRearZone: isRightPlacement,
        isFrontZone: isLeftPlacement,
        isCentralLeftZone: zoneKeyUpper === "CENTRAL LEFT",
        isRearLeftZone: zoneKeyUpper === "REAR LEFT",
        isRearRightZone: zoneKeyUpper === "REAR RIGHT",
        background: swmBg,
      },
      position: { x: sx, y: sy },
      style: {
        width: swmSize,
        height: swmSize,
        boxShadow: isSwmSelected ? "0 0 0 3px #000000" : "none",
      },
    });

    const { sourcePosition, targetPosition } =
      getSwmEdgePositions(zoneKeyUpper);

    edges.push({
      id: `${ee.id}-${swm.id}`,
      source: ee.id,
      target: swm.id,
      type: "straight",
      animated: false,
      style: { stroke: "#333", strokeWidth: 2 },
      sourcePosition,
      targetPosition,
    });
  }
};

const getGridEeSize = (baseEeSize, minEeSize, count, shrinkAfter) =>
  count > shrinkAfter
    ? Math.max(minEeSize, baseEeSize - (count - shrinkAfter) * 8)
    : baseEeSize;

const getHorizontalRowPositions = ({
  count,
  zonePos,
  baseEeSize,
  minEeSize,
  gap,
  placeLeft,
}) => {
  const positions = [];
  if (count <= 0) return positions;

  const gridEeSize = getGridEeSize(baseEeSize, minEeSize, count, 6);
  const hSpacing = gridEeSize + gap;
  const extraHorizontalMargin = 220;
  const halfZone = Math.max(nodeWidth, nodeHeight) / 2;
  const y = zonePos.y;

  if (placeLeft) {
    const gridRightEdge = zonePos.x - halfZone - gap - extraHorizontalMargin;
    const startX = gridRightEdge - (count - 1) * hSpacing - gridEeSize / 2;
    for (let c = 0; c < count; c++) {
      const x = startX + c * hSpacing + gridEeSize / 2;
      positions[c] = { x, y, size: gridEeSize };
    }
    return positions;
  }

  const gridLeftEdge = zonePos.x + halfZone + gap + extraHorizontalMargin;
  const startX = gridLeftEdge + gridEeSize / 2;
  for (let c = 0; c < count; c++) {
    const x = startX + c * hSpacing;
    positions[c] = { x, y, size: gridEeSize };
  }
  return positions;
};

const getVerticalColumnPositions = ({
  count,
  zonePos,
  baseEeSize,
  minEeSize,
  gap,
  placeAbove,
}) => {
  const positions = [];
  if (count <= 0) return positions;

  const gridEeSize = getGridEeSize(baseEeSize, minEeSize, count, 4);
  const vSpacing = gridEeSize + gap;
  const extraVerticalMargin = 180;
  const halfZone = Math.max(nodeWidth, nodeHeight) / 2;
  const x = zonePos.x;

  if (placeAbove) {
    const gridBottomEdge = zonePos.y - halfZone - gap - extraVerticalMargin;
    const startY = gridBottomEdge - (count - 1) * vSpacing - gridEeSize / 2;
    for (let r = 0; r < count; r++) {
      const y = startY + r * vSpacing + gridEeSize / 2;
      positions[r] = { x, y, size: gridEeSize };
    }
    return positions;
  }

  const gridTopEdge = zonePos.y + halfZone + gap + extraVerticalMargin;
  const startY = gridTopEdge + gridEeSize / 2;
  for (let r = 0; r < count; r++) {
    const y = startY + r * vSpacing;
    positions[r] = { x, y, size: gridEeSize };
  }
  return positions;
};

const getLayeredAnchorPositions = ({
  ees,
  zonePos,
  baseEeSize,
  minEeSize,
  gap,
  anchorDirs,
}) => {
  const positions = [];
  const slotsPerLayer = anchorDirs.length;

  for (const [idx] of ees.entries()) {
    const layer = Math.floor(idx / slotsPerLayer);
    const slot = idx % slotsPerLayer;
    const currentEeSize = Math.max(minEeSize, baseEeSize - layer * 12);
    const baseOffset =
      Math.max(nodeWidth, nodeHeight) / 2 + gap + layer * (currentEeSize + gap + 10);
    const dir = anchorDirs[slot];
    const x = zonePos.x + dir.x * baseOffset;
    const y = zonePos.y + dir.y * baseOffset;
    positions[idx] = { x, y, size: currentEeSize };
  }

  return positions;
};

const getEePositionsForZone = ({ zoneKeyUpper, ees, zonePos }) => {
  const count = ees.length;
  const baseEeSize = Math.min(nodeWidth, 140);
  const minEeSize = 50;
  const gap = 20;

  if (
    zoneKeyUpper === "FRONT LEFT" ||
    zoneKeyUpper === "FRONT RIGHT" ||
    zoneKeyUpper === "REAR LEFT" ||
    zoneKeyUpper === "REAR RIGHT" ||
    zoneKeyUpper === "CENTRAL LEFT" ||
    zoneKeyUpper === "CENTRAL RIGHT"
  ) {
    const placeLeft =
      zoneKeyUpper === "FRONT LEFT" ||
      zoneKeyUpper === "REAR LEFT" ||
      zoneKeyUpper === "CENTRAL LEFT";
    return getHorizontalRowPositions({
      count,
      zonePos,
      baseEeSize,
      minEeSize,
      gap,
      placeLeft,
    });
  }

  if (zoneKeyUpper === "FRONT") {
    return getVerticalColumnPositions({
      count,
      zonePos,
      baseEeSize,
      minEeSize,
      gap,
      placeAbove: true,
    });
  }

  if (zoneKeyUpper === "REAR") {
    return getVerticalColumnPositions({
      count,
      zonePos,
      baseEeSize,
      minEeSize,
      gap,
      placeAbove: false,
    });
  }

  const anchorDirs = PER_ZONE_ANCHORS[zoneKeyUpper] || DEFAULT_ANCHOR_DIRS;
  return getLayeredAnchorPositions({
    ees,
    zonePos,
    baseEeSize,
    minEeSize,
    gap,
    anchorDirs,
  });
};

const buildZoneNodeId = (zone) => {
  const seed =
    zone?.zone_detail_id ?? zone?.sequence ?? zone?.position ?? zone?.zone_name;
  const safeSeed = (seed ?? zone?._fallback_index ?? "unknown")
    .toString()
    .trim()
    .replaceAll(/\s+/g, "-")
    .toLowerCase();
  return `zone-${safeSeed}`;
};

const getZonesSource = (projectDetails) => {
  const zonesFromZoneDetails = Array.isArray(projectDetails?.zone_details)
    ? projectDetails.zone_details
    : [];

  if (zonesFromZoneDetails.length > 0) {
    return zonesFromZoneDetails.map((zoneDetail, index) => ({
      zone_name: zoneDetail?.zone?.zone_name || zoneDetail?.zone_name || "",
      position: zoneDetail?.zone?.position || zoneDetail?.position || "",
      sequence: zoneDetail?.zone?.sequence ?? zoneDetail?.sequence,
      zone_detail_id: zoneDetail?.id ?? zoneDetail?.zone?.id,
      _fallback_index: index + 1,
    }));
  }

  const fallbackZones = Array.isArray(projectDetails?.zones) ? projectDetails.zones : [];
  return fallbackZones.map((zone, index) => ({
    zone_name: zone?.zone_name || "",
    position: zone?.position || "",
    sequence: zone?.sequence,
    zone_detail_id: zone?.id,
    _fallback_index: index + 1,
  }));
};

const findZoneNodeIdForEe = (ee, projectDetails) => {
  const zonesSource = getZonesSource(projectDetails);
  if (zonesSource.length === 0) return null;

  const eeZoneDetails = ee?.zone_details || {};
  const eeSequence = eeZoneDetails?.sequence ?? eeZoneDetails?.zone?.sequence;
  const eeZoneName = (
    eeZoneDetails?.zone_name ||
    eeZoneDetails?.zone?.zone_name ||
    ""
  )
    .toString()
    .trim()
    .toUpperCase();
  const eeZonePosition = (
    eeZoneDetails?.position ||
    eeZoneDetails?.zone?.position ||
    ""
  )
    .toString()
    .trim()
    .toUpperCase();

  const matchedZone = zonesSource.find((zone) => {
    const zoneSequence = zone?.sequence;
    const zoneName = (zone?.zone_name || "").toString().trim().toUpperCase();
    const zonePosition = (zone?.position || "").toString().trim().toUpperCase();

    const bySequence =
      zoneSequence != null &&
      eeSequence != null &&
      Number(zoneSequence) === Number(eeSequence);
    const byName = !!zoneName && !!eeZoneName && zoneName === eeZoneName;
    const byPosition =
      !!zonePosition && !!eeZonePosition && zonePosition === eeZonePosition;

    return bySequence || byName || byPosition;
  });

  return matchedZone ? buildZoneNodeId(matchedZone) : null;
};

const toUpperTrim = (value) =>
  (value ?? "").toString().trim().toUpperCase();

const getZoneMatchKeys = (zone) => ({
  sequence: zone?.sequence,
  name: toUpperTrim(zone?.zone_name),
  position: toUpperTrim(zone?.position),
});

const getEeZoneMatchKeys = (ee) => {
  const eeZoneDetails = ee?.zone_details || {};
  return {
    sequence: eeZoneDetails?.sequence ?? eeZoneDetails?.zone?.sequence,
    name: toUpperTrim(eeZoneDetails?.zone_name || eeZoneDetails?.zone?.zone_name),
    position: toUpperTrim(eeZoneDetails?.position || eeZoneDetails?.zone?.position),
  };
};

const isSameZone = (zoneKeys, eeKeys) => {
  const bySequence =
    zoneKeys.sequence != null &&
    eeKeys.sequence != null &&
    Number(zoneKeys.sequence) === Number(eeKeys.sequence);
  const byName = !!zoneKeys.name && !!eeKeys.name && zoneKeys.name === eeKeys.name;
  const byPosition =
    !!zoneKeys.position &&
    !!eeKeys.position &&
    zoneKeys.position === eeKeys.position;
  return bySequence || byName || byPosition;
};

const getSwmChildren = (projectDetails, ee) => {
  const swmDetails = Array.isArray(projectDetails?.swm_details)
    ? projectDetails.swm_details
    : [];
  return swmDetails
    .filter((swm) => swm?.ee_id === ee.ee_id && swm?.version !== "")
    .map((swm) => ({
      id: `swm-${swm.swm_id}`,
      name: swm.name,
      version: swm.version,
      linkedSwmIds: swm.linked_swm_ids?.map((obj) => obj.id) || [],
    }));
};

const getEeChildrenForZone = (projectDetails, zone) => {
  const zoneKeys = getZoneMatchKeys(zone);
  const eeDetails = Array.isArray(projectDetails?.ee_details)
    ? projectDetails.ee_details
    : [];

  return eeDetails
    .filter((ee) => {
      if (ee?.version === "") return false;
      const eeKeys = getEeZoneMatchKeys(ee);
      return isSameZone(zoneKeys, eeKeys);
    })
    .map((ee) => ({
      id: `ee-${ee.ee_id}`,
      name: ee.description,
      version: ee.version,
      children: getSwmChildren(projectDetails, ee),
    }));
};

const createHierarchyData = (projectDetails) => {
  const zonesSource = getZonesSource(projectDetails);

  return {
    id: `project-${projectDetails.project_id}`,
    name: projectDetails.name,
    children: zonesSource.map((zone) => ({
      id: buildZoneNodeId(zone),
      name: zone.zone_name,
      children: getEeChildrenForZone(projectDetails, zone),
    })),
  };
};

const getMousePosition = (event) => ({
  x: event.clientX || event.pageX,
  y: event.clientY || event.pageY,
});

// getSwmId and getEeId declared above

const findSwmDetails = (swmIdNum, swmDetailsList) =>
  swmDetailsList?.find((s) => Number(s.swm_id) === swmIdNum);

const findEeDetails = (eeIdNum, eeDetailsList) =>
  eeDetailsList?.find((ee) => Number(ee.ee_id) === eeIdNum);

const getLinkedSwmInfo = ({
  nodeId,
  selectedNode,
  mindMapProjectDetails,
  swmIdNum,
}) => {
  let isLinkedSWM = false;
  let networkInterfaceInfo = null;
  let networkInterfaceColor = null;

  if (
    selectedNode?.startsWith("swm-") &&
    selectedNode !== nodeId
  ) {
    const selectedSwmIdNum = Number(selectedNode.replaceAll("swm-", ""));

    const selectedSwmDetails = mindMapProjectDetails.swm_details?.find(
      (s) => Number(s.swm_id) === selectedSwmIdNum,
    );

    if (selectedSwmDetails?.linked_swm_ids) {
      const linkedSWMObj = selectedSwmDetails.linked_swm_ids.find(
        (linkedObj) => Number(linkedObj.id) === swmIdNum,
      );

      if (linkedSWMObj) {
        isLinkedSWM = true;
        networkInterfaceInfo =
          linkedSWMObj.network_interface || "Network not defined";
        networkInterfaceColor = linkedSWMObj.color || null;
      }
    }
  }

  return { isLinkedSWM, networkInterfaceInfo, networkInterfaceColor };
};

const buildEnhancedSwmDetails = (
  swmDetails,
  isLinkedSWM,
  networkInterfaceInfo,
  networkInterfaceColor,
  networkInterfacesList = [],
) => ({
  ...swmDetails,
  network_interfaces:
    networkInterfacesList.length > 0
      ? networkInterfacesList
      : networkInterfaceInfo
        ? [{ interface_name: networkInterfaceInfo, color: networkInterfaceColor }]
        : [],
  network_interface: isLinkedSWM
    ? {
      interface_name: networkInterfaceInfo,
      protocol: networkInterfaceInfo,
      color: networkInterfaceColor,
    }
    : swmDetails?.network_interface,
});

const handleSwmHover = ({
  node,
  mousePos,
  mindMapProjectDetails,
  selectedNode,
  setHoverDialog,
  filterMatches,
}) => {
  const swmIdNum = getSwmId(node.id);

  const swmDetails = findSwmDetails(
    swmIdNum,
    mindMapProjectDetails.swm_details,
  );

  if (!swmDetails) return;

  let { isLinkedSWM, networkInterfaceInfo, networkInterfaceColor } = getLinkedSwmInfo({
    nodeId: node.id,
    selectedNode,
    mindMapProjectDetails,
    swmIdNum,
  });

  let networkInterfacesList = [];

  if (filterMatches?.isFilterActive) {
    const relevantLinks = (filterMatches.matchingSwmLinks || []).filter(
      (link) => link.source === node.id || link.target === node.id,
    );

    const interfacesMap = new Map();
    relevantLinks.forEach((link) => {
      if (link.network_interface && !interfacesMap.has(link.network_interface)) {
        interfacesMap.set(link.network_interface, link.color || "#000000");
      }
    });

    if (Array.isArray(swmDetails.linked_swm_ids)) {
      swmDetails.linked_swm_ids.forEach((linkObj) => {
        const net = linkObj?.network_interface;
        if (net && !interfacesMap.has(net)) {
          const isMatched =
            filterMatches.filterSwm &&
            (filterMatches.network_interfaces_all ||
              (filterMatches.matchingSwmLinks || []).some(
                (l) => (l.network_interface || "").toLowerCase() === net.toLowerCase(),
              ));
          if (isMatched) {
            interfacesMap.set(net, linkObj.color || "#000000");
          }
        }
      });
    }

    if (interfacesMap.size > 0) {
      isLinkedSWM = true;
      networkInterfacesList = Array.from(interfacesMap.entries()).map(
        ([interface_name, color]) => ({ interface_name, color }),
      );
      if (!networkInterfaceInfo && networkInterfacesList.length > 0) {
        networkInterfaceInfo = networkInterfacesList[0].interface_name;
        networkInterfaceColor = networkInterfacesList[0].color;
      }
    }
  }

  const enhancedSwmDetails = buildEnhancedSwmDetails(
    swmDetails,
    isLinkedSWM,
    networkInterfaceInfo,
    networkInterfaceColor,
    networkInterfacesList,
  );

  const clampedPos = calculateClampedPosition(mousePos);

  setHoverDialog({
    open: true,
    swmDetails: enhancedSwmDetails,
    position: clampedPos,
    isLinkedSWM,
  });
};

const handleEeHover = ({
  node,
  mousePos,
  mindMapProjectDetails,
  selectedNode,
  setEeHoverDialog,
  filterMatches,
}) => {
  const eeIdNum = getEeId(node.id);

  const eeDetails = findEeDetails(eeIdNum, mindMapProjectDetails.ee_details);

  if (!eeDetails) return;

  let { isLinkedEE, networkInterfaceInfo, networkInterfaceColor } = getLinkedEeInfo({
    nodeId: node.id,
    selectedNode,
    mindMapProjectDetails,
    eeIdNum,
  });

  let networkInterfacesList = [];

  if (filterMatches?.isFilterActive) {
    const relevantLinks = (filterMatches.matchingEeLinks || []).filter(
      (link) => link.source === node.id || link.target === node.id,
    );

    const interfacesMap = new Map();
    relevantLinks.forEach((link) => {
      if (link.network_interface && !interfacesMap.has(link.network_interface)) {
        interfacesMap.set(link.network_interface, link.color || "#000000");
      }
    });

    if (Array.isArray(eeDetails.linked_ee_ids)) {
      eeDetails.linked_ee_ids.forEach((linkObj) => {
        const net = typeof linkObj === "object" ? linkObj?.network_interface : null;
        if (net && !interfacesMap.has(net)) {
          const isMatched =
            filterMatches.filterEe &&
            (filterMatches.network_interfaces_all ||
              (filterMatches.matchingEeLinks || []).some(
                (l) => (l.network_interface || "").toLowerCase() === net.toLowerCase(),
              ));
          if (isMatched) {
            interfacesMap.set(net, (typeof linkObj === "object" ? linkObj.color : null) || "#000000");
          }
        }
      });
    }

    if (interfacesMap.size > 0) {
      isLinkedEE = true;
      networkInterfacesList = Array.from(interfacesMap.entries()).map(
        ([interface_name, color]) => ({ interface_name, color }),
      );
      if (!networkInterfaceInfo && networkInterfacesList.length > 0) {
        networkInterfaceInfo = networkInterfacesList[0].interface_name;
        networkInterfaceColor = networkInterfacesList[0].color;
      }
    }
  }

  const enhancedEeDetails = buildEnhancedEeDetails(
    eeDetails,
    isLinkedEE,
    networkInterfaceInfo,
    networkInterfaceColor,
    networkInterfacesList,
  );

  const clampedPos = calculateClampedPosition(mousePos);

  setEeHoverDialog({
    open: true,
    eeDetails: enhancedEeDetails,
    position: clampedPos,
    isLinkedEE,
  });
};

const getLinkedEeInfo = ({
  nodeId,
  selectedNode,
  mindMapProjectDetails,
  eeIdNum,
}) => {
  let isLinkedEE = false;
  let networkInterfaceInfo = null;
  let networkInterfaceColor = null;

  if (selectedNode?.startsWith("ee-") && selectedNode !== nodeId) {
    const selectedEeIdNum = Number(selectedNode.replaceAll("ee-", ""));

    const selectedEeDetails = mindMapProjectDetails.ee_details?.find(
      (ee) => Number(ee.ee_id) === selectedEeIdNum,
    );

    if (selectedEeDetails?.linked_ee_ids) {
      const linkedEeObj = selectedEeDetails.linked_ee_ids.find((linkedObj) => {
        const id =
          typeof linkedObj === "object"
            ? (linkedObj.id ?? linkedObj.ee_id)
            : linkedObj;
        return Number(id) === eeIdNum;
      });

      if (linkedEeObj) {
        isLinkedEE = true;
        networkInterfaceInfo =
          typeof linkedEeObj === "object"
            ? linkedEeObj.network_interface || "Network not defined"
            : "Network not defined";
        networkInterfaceColor =
          typeof linkedEeObj === "object" ? linkedEeObj.color || null : null;
      }
    }
  }

  return { isLinkedEE, networkInterfaceInfo, networkInterfaceColor };
};

const buildEnhancedEeDetails = (
  eeDetails,
  isLinkedEE,
  networkInterfaceInfo,
  networkInterfaceColor,
  networkInterfacesList = [],
) => ({
  ...eeDetails,
  network_interfaces:
    networkInterfacesList.length > 0
      ? networkInterfacesList
      : networkInterfaceInfo
        ? [{ interface_name: networkInterfaceInfo, color: networkInterfaceColor }]
        : [],
  network_interface: isLinkedEE
    ? {
      interface_name: networkInterfaceInfo,
      protocol: networkInterfaceInfo,
      color: networkInterfaceColor,
    }
    : eeDetails?.network_interface,
});

const isEeLinkedToSelected = (eeIdNum, selectedNodeId, eeDetailsList) => {
  if (!selectedNodeId?.startsWith("ee-")) return false;
  const selEeNum = Number(selectedNodeId.replaceAll("ee-", ""));
  if (selEeNum === eeIdNum) return false;

  const selEe = eeDetailsList?.find((e) => Number(e.ee_id) === selEeNum);
  if (!selEe?.linked_ee_ids) return false;

  return selEe.linked_ee_ids.some((item) => {
    const id = typeof item === "object" ? (item.id ?? item.ee_id) : item;
    return Number(id) === eeIdNum;
  });
};

const handleEeClick = ({
  node,
  expandedNodes,
  mindMapProjectDetails,
  setExpandedNodes,
  setLastClickedZoneId,
}) => {
  if (!mindMapProjectDetails?.ee_details) return;

  const newExpanded = new Set(expandedNodes);

  // Close / hide all software modules so only linked EE components are shown
  for (const id of newExpanded) {
    if (id.startsWith("ee-")) {
      newExpanded.delete(id);
    }
  }

  const eeIdNum = Number(node.id.replaceAll("ee-", ""));
  const eeObj = mindMapProjectDetails.ee_details.find(
    (ee) => Number(ee.ee_id) === eeIdNum,
  );

  if (!eeObj) {
    setExpandedNodes(newExpanded);
    return;
  }

  const selfZoneNodeId = findZoneNodeIdForEe(eeObj, mindMapProjectDetails);
  if (selfZoneNodeId) newExpanded.add(selfZoneNodeId);

  let openedNewZone = false;
  const linkedList = eeObj.linked_ee_ids || [];
  for (const item of linkedList) {
    const linkedEeId =
      typeof item === "object" ? (item.id ?? item.ee_id) : item;
    const linkedEeObj = mindMapProjectDetails.ee_details.find(
      (ee) => Number(ee.ee_id) === Number(linkedEeId),
    );
    if (!linkedEeObj) continue;

    const zoneNodeId = findZoneNodeIdForEe(linkedEeObj, mindMapProjectDetails);
    if (zoneNodeId) {
      if (!newExpanded.has(zoneNodeId)) {
        openedNewZone = true;
      }
      newExpanded.add(zoneNodeId);
    }
  }

  setExpandedNodes(newExpanded);

  if (openedNewZone && setLastClickedZoneId) {
    setLastClickedZoneId(node.id);
  }
};

const isZoneNode = (id) => id.startsWith("zone-");
const isEeNode = (id) => id.startsWith("ee-");
const isSwmNode = (id) => id.startsWith("swm-");

const toggleExpansion = (nodeId, expandedSet) => {
  const newExpanded = new Set(expandedSet);
  const willOpen = !newExpanded.has(nodeId);

  if (willOpen) newExpanded.add(nodeId);
  else newExpanded.delete(nodeId);

  return { newExpanded, willOpen };
};

const expandEeAndZone = ({ eeId, newExpanded, mindMapProjectDetails }) => {
  const eeNodeId = `ee-${eeId}`;
  newExpanded.add(eeNodeId);

  const parentEeObj = mindMapProjectDetails.ee_details.find(
    (ee) => Number(ee.ee_id) === Number(eeId),
  );

  if (!parentEeObj) return;

  const zoneNodeId = findZoneNodeIdForEe(parentEeObj, mindMapProjectDetails);

  if (zoneNodeId) newExpanded.add(zoneNodeId);
};

const handleZoneOrEeClick = ({
  node,
  expandedNodes,
  setExpandedNodes,
  setLastClickedZoneId,
}) => {
  const { newExpanded, willOpen } = toggleExpansion(node.id, expandedNodes);

  setExpandedNodes(newExpanded);

  if ((isZoneNode(node.id) || isEeNode(node.id)) && willOpen) {
    setLastClickedZoneId(node.id);
  }
};

const handleSwmClick = ({
  node,
  expandedNodes,
  mindMapProjectDetails,
  setExpandedNodes,
}) => {
  if (!mindMapProjectDetails?.swm_details) return;

  const newExpanded = new Set(expandedNodes);

  const swmIdNum = Number(node.id.replaceAll("swm-", ""));
  const swmObj = mindMapProjectDetails.swm_details.find(
    (s) => Number(s.swm_id) === swmIdNum,
  );

  if (!swmObj) return;

  expandEeAndZone({
    eeId: swmObj.ee_id,
    newExpanded,
    mindMapProjectDetails,
  });

  const linkedIds = swmObj.linked_swm_ids?.map((obj) => obj.id) || [];

  for (const lid of linkedIds) {
    const linkedSwm = mindMapProjectDetails.swm_details.find(
      (s) => Number(s.swm_id) === Number(lid),
    );

    if (!linkedSwm) continue;

    expandEeAndZone({
      eeId: linkedSwm.ee_id,
      newExpanded,
      mindMapProjectDetails,
    });
  }

  setExpandedNodes(newExpanded);
};

const ZonalMindMapDialog = ({
  open,
  onClose,
  selectedProjectId,
  selectedVariantId,
  projectName = "Project Mind Map",
}) => {
  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: "95vw",
          height: "90vh",
          maxWidth: "none",
          maxHeight: "none",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h6" component="div">
          {projectName} - Zonal Mind Map
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, overflow: "hidden" }}>
        <VlcZonalMindMap
          selectedProjectId={selectedProjectId}
          selectedVariantId={selectedVariantId}
          isDialog={true}
        />
      </DialogContent>
    </Dialog>
  );
};

const ZONE_POSITIONS = [
  { x: -320, y: -220 }, // top-left
  { x: 0, y: -360 }, // top
  { x: 320, y: -220 }, // top-right
  { x: 420, y: 0 }, // right
  { x: 320, y: 220 }, // bottom-right
  { x: 0, y: 360 }, // bottom
  { x: -320, y: 220 }, // bottom-left
  { x: -420, y: 0 }, // left
];

const ZONE_NAME_TO_POS = {
  "FRONT LEFT": { x: -320, y: -220 },
  "FRONT RIGHT": { x: 320, y: -220 },
  "REAR LEFT": { x: -320, y: 220 },
  "REAR RIGHT": { x: 320, y: 220 },
  "CENTRAL LEFT": { x: -420, y: 0 },
  "CENTRAL RIGHT": { x: 420, y: 0 },
  FRONT: { x: 0, y: -360 },
  REAR: { x: 0, y: 360 },
};

const ZONE_SOURCE_HANDLE_MAP = {
  "FRONT LEFT": "source-top-left",
  "FRONT RIGHT": "source-top-right",
  FRONT: "source-top",
  "REAR LEFT": "source-bottom-left",
  "REAR RIGHT": "source-bottom-right",
  REAR: "source-bottom",
  "CENTRAL LEFT": "source-left",
  "CENTRAL RIGHT": "source-right",
};

const isRearZoneType = (nameKey) =>
  nameKey === "REAR LEFT" || nameKey === "REAR RIGHT";

const shouldRotateZone = (nameKey) =>
  nameKey === "REAR LEFT" ||
  nameKey === "REAR RIGHT" ||
  nameKey === "REAR";

const getZoneSourceHandle = (nameKey) =>
  ZONE_SOURCE_HANDLE_MAP[nameKey] || "source-bottom";

const getZonePosition = (nameKey, i, offsets = {}) => {
  const pos =
    ZONE_NAME_TO_POS[nameKey] || ZONE_POSITIONS[i % ZONE_POSITIONS.length];

  if (nameKey === "REAR LEFT" && offsets.rearLeftYOffset > 0) {
    return { ...pos, y: pos.y + offsets.rearLeftYOffset };
  }
  if (nameKey === "FRONT RIGHT" && offsets.frontRightYOffset > 0) {
    return { ...pos, y: pos.y - offsets.frontRightYOffset };
  }
  if (nameKey === "REAR RIGHT" && offsets.rearRightXOffset > 0) {
    return { ...pos, x: pos.x + offsets.rearRightXOffset };
  }
  if (nameKey === "FRONT LEFT" && offsets.frontLeftXOffset > 0) {
    return { ...pos, x: pos.x - offsets.frontLeftXOffset };
  }
  return pos;
};

const getZoneTargetConfig = (nameKey) => {
  if (
    nameKey === "REAR LEFT" ||
    nameKey === "REAR RIGHT" ||
    nameKey === "FRONT"
  ) {
    return {
      targetHandle: "zone-bottom-target",
      targetPosition: Position.Bottom,
    };
  }
  if (nameKey === "REAR") {
    return {
      targetHandle: "zone-top-target",
      targetPosition: Position.Top,
    };
  }
  return {
    targetHandle: undefined,
    targetPosition: Position.Top,
  };
};

const getEeSourcePosition = (zoneKeyUpper) => {
  if (zoneKeyUpper === "REAR") return Position.Right;
  if (zoneKeyUpper === "FRONT") return Position.Left;
  if (isBottomFlowZone(zoneKeyUpper)) return Position.Bottom;
  return Position.Top;
};

const buildSwmMappings = (zones) => {
  const swmLinkedMap = new Map();
  const swmParentEeMap = new Map();

  for (const z of zones) {
    for (const ee of z.children || []) {
      for (const swm of ee.children || []) {
        const swmNum = Number(String(swm.id || "").replaceAll("swm-", ""));
        swmLinkedMap.set(swmNum, swm.linkedSwmIds || []);
        swmParentEeMap.set(swmNum, ee.id);
      }
    }
  }

  return { swmLinkedMap, swmParentEeMap };
};

const generateZoneNodesAndEdges = (zones, projectId, offsets) => {
  const nodes = [];
  const edges = [];
  const zonePositionsMap = new Map();

  for (const [i, zone] of zones.entries()) {
    const nameKey = getZoneNameKey(zone);
    const pos = getZonePosition(nameKey, i, offsets);
    zonePositionsMap.set(zone.id, pos);

    nodes.push({
      id: zone.id,
      type: "zoneNode",
      data: {
        labelText: zone.name,
        rotate: shouldRotateZone(nameKey),
        isFromFrontSide: isFrontZone(nameKey),
        isRear: isRearZoneType(nameKey),
      },
      position: { x: pos.x, y: pos.y },
      style: { width: nodeWidth, height: nodeHeight },
      sourcePosition: Position.Top,
      targetPosition: Position.Top,
    });

    const { targetHandle, targetPosition } = getZoneTargetConfig(nameKey);

    edges.push({
      id: `${projectId}-${zone.id}`,
      source: projectId,
      target: zone.id,
      type: "step",
      animated: false,
      style: { stroke: "#333", strokeWidth: 2 },
      sourceHandle: getZoneSourceHandle(nameKey),
      targetHandle,
      targetPosition,
    });
  }

  return { nodes, edges, zonePositionsMap };
};

const getEeTogglePosition = (sourcePos, btnSize) => {
  const dist = `-${btnSize + 3}px`;
  switch (sourcePos) {
    case Position.Top:
      return { top: dist, left: "50%", transform: "translateX(-50%)" };
    case Position.Bottom:
      return { bottom: dist, left: "50%", transform: "translateX(-50%)" };
    case Position.Left:
      return { left: dist, top: "50%", transform: "translateY(-50%)" };
    case Position.Right:
      return { right: dist, top: "50%", transform: "translateY(-50%)" };
    default:
      return { bottom: dist, left: "50%", transform: "translateX(-50%)" };
  }
};

const generateEeNodesAndEdgesForZone = ({
  zone,
  zonePos,
  expandedNodes,
  selectedNodeId,
  mindMapProjectDetails,
  swmLinkedMap,
  swmParentEeMap,
  nodes,
  edges,
  onToggleEe,
  filterMatches,
  focusedFilter,
}) => {
  const ees = zone.children || [];
  if (ees.length === 0) return;

  const visibleEes = filterMatches?.isFilterActive
    ? ees.filter((ee) => {
        const eeIdNum = getEeId(ee.id);
        if (focusedFilter) {
          return focusedFilter.visibleEeIds.has(eeIdNum);
        }
        const matchesEe =
          filterMatches.filterEe && filterMatches.matchingEeIds.has(eeIdNum);
        const hasMatchingSwm =
          filterMatches.filterSwm &&
          (ee.children || []).some((swm) =>
            filterMatches.matchingSwmIds.has(getSwmId(swm.id)),
          );
        return matchesEe || hasMatchingSwm;
      })
    : ees;

  if (visibleEes.length === 0) return;

  const zoneKeyUpper = getZoneNameKey(zone);
  const positions = getEePositionsForZone({
    zoneKeyUpper,
    ees: visibleEes,
    zonePos,
  });

  const eeSourcePosition = getEeSourcePosition(zoneKeyUpper);

  for (const [idx, ee] of visibleEes.entries()) {
    const posInfo = positions[idx];
    if (!posInfo) continue;

    const { x, y, size: currentEeSize } = posInfo;
    const hasChildren = Boolean(ee.children && ee.children.length > 0);
    const isExpanded = expandedNodes.has(ee.id);
    const btnSize = currentEeSize < 80 ? 24 : 28;
    const iconSize = currentEeSize < 80 ? 13 : 15;
    const togglePosStyle = getEeTogglePosition(eeSourcePosition, btnSize);

    const eeIdNum = getEeId(ee.id);
    const isSelected = ee.id === selectedNodeId;
    const isLinkedEE = isEeLinkedToSelected(
      eeIdNum,
      selectedNodeId,
      mindMapProjectDetails?.ee_details,
    );

    const isFilterMatchedEe =
      filterMatches?.isFilterActive &&
      filterMatches.filterEe &&
      (focusedFilter
        ? (focusedFilter.type === "ee" && focusedFilter.visibleEeIds.has(eeIdNum))
        : filterMatches.matchingEeIds.has(eeIdNum));

    let eeBackground = "black";
    let eeColor = "white";
    if (isSelected) {
      eeBackground = (filterMatches?.isFilterActive && focusedFilter?.type === "ee")
        ? "#C4D600"
        : "#D0D0CE";
      eeColor = "black";
    } else if (isFilterMatchedEe) {
      eeBackground = "#C4D600";
      eeColor = "black";
    } else if (isLinkedEE) {
      eeBackground = "#C4D600";
      eeColor = "black";
    }

    nodes.push({
      id: ee.id,
      data: {
        label: (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "6px",
              boxSizing: "border-box",
              position: "relative",
            }}
          >
            <div style={CLAMPED_NODE_LABEL_STYLE}>
              {ee.name}
            </div>
            {hasChildren && (
              <div
                className="nodrag nopan"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleEe?.(ee.id);
                }}
                title={isExpanded ? "Hide software modules" : "Show software modules"}
                style={{
                  position: "absolute",
                  ...togglePosStyle,
                  width: btnSize,
                  height: btnSize,
                  minWidth: btnSize,
                  minHeight: btnSize,
                  borderRadius: "50%",
                  background: "#ffffff",
                  color: "#26890D",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
                  border: "2px solid #26890D",
                  flexShrink: 0,
                  userSelect: "none",
                  zIndex: 10,
                  transition: "transform 0.15s ease, background-color 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = `${togglePosStyle.transform} scale(1.18)`;
                  e.currentTarget.style.backgroundColor = "#f0fdf4";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = togglePosStyle.transform;
                  e.currentTarget.style.backgroundColor = "#ffffff";
                }}
              >
                {isExpanded ? (
                  <svg
                    width={iconSize}
                    height={iconSize}
                    viewBox="0 0 10 10"
                    style={{ display: "block" }}
                  >
                    <line
                      x1="2"
                      y1="5"
                      x2="8"
                      y2="5"
                      stroke="#26890D"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width={iconSize}
                    height={iconSize}
                    viewBox="0 0 10 10"
                    style={{ display: "block" }}
                  >
                    <line
                      x1="5"
                      y1="2"
                      x2="5"
                      y2="8"
                      stroke="#26890D"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                    />
                    <line
                      x1="2"
                      y1="5"
                      x2="8"
                      y2="5"
                      stroke="#26890D"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
              </div>
            )}
          </div>
        ),
      },
      position: { x, y },
      targetPosition: isFrontZone(zoneKeyUpper)
        ? Position.Bottom
        : Position.Top,
      sourcePosition: eeSourcePosition,
      style: {
        width: currentEeSize,
        height: currentEeSize,
        borderRadius: "50%",
        background: eeBackground,
        color: eeColor,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "visible",
        cursor: "pointer",
        boxShadow: isSelected && (!focusedFilter || focusedFilter.type === "ee") ? "0 0 0 3.5px #000000" : "none",
      },
    });

    const { sourceHandle, sourcePosition } = getEeSourceConfig(zoneKeyUpper);
    const fromFront = isFrontZone(zoneKeyUpper);

    edges.push({
      id: `${zone.id}-${ee.id}`,
      source: zone.id,
      target: ee.id,
      type: "step",
      sourceHandle,
      sourcePosition,
      targetPosition: fromFront ? Position.Bottom : Position.Top,
      animated: false,
      style: { stroke: "#333", strokeWidth: 2 },
    });

    if (expandedNodes.has(ee.id) && ee.children?.length > 0) {
      renderSwms({
        ee,
        nodes,
        edges,
        zoneKeyUpper,
        selectedNodeId,
        swmLinkedMap,
        swmParentEeMap,
        x,
        y,
        currentEeSize,
        filterMatches,
        focusedFilter,
      });
    }
  }
};

const computeFilterMatches = (filters, mindMapProjectDetails) => {
  const isTypeActive = Boolean(
    filters?.type?.all || filters?.type?.ee_component || filters?.type?.software_module
  );
  const isNetworkActive = Boolean(
    filters?.network_interfaces_all ||
      (Array.isArray(filters?.network_interfaces) && filters.network_interfaces.length > 0)
  );

  const isFilterActive = isTypeActive && isNetworkActive;

  if (!isFilterActive || !mindMapProjectDetails) {
    return {
      isFilterActive: false,
      matchingEeIds: new Set(),
      matchingSwmIds: new Set(),
      parentEeIdsForSwms: new Set(),
      matchingZoneIds: new Set(),
      matchingEeLinks: [],
      matchingSwmLinks: [],
      filterEe: false,
      filterSwm: false,
    };
  }

  const filterEe = Boolean(filters?.type?.all || filters?.type?.ee_component);
  const filterSwm = Boolean(filters?.type?.all || filters?.type?.software_module);

  const isNetworkInterfaceMatched = (netInterface) => {
    if (!netInterface) return false;
    const clean = netInterface.trim().toLowerCase();
    if (clean === "network not defined") return false;
    if (filters?.network_interfaces_all) return true;
    if (Array.isArray(filters?.network_interfaces)) {
      return filters.network_interfaces.some(
        (ni) => (ni || "").trim().toLowerCase() === clean
      );
    }
    return false;
  };

  const matchingEeIds = new Set();
  const matchingSwmIds = new Set();
  const parentEeIdsForSwms = new Set();
  const matchingZoneIds = new Set();

  const matchingEeLinks = [];
  const seenEeLinks = new Set();

  if (filterEe && Array.isArray(mindMapProjectDetails.ee_details)) {
    mindMapProjectDetails.ee_details.forEach((ee) => {
      const srcEeId = Number(ee.ee_id);
      if (!Array.isArray(ee.linked_ee_ids)) return;

      ee.linked_ee_ids.forEach((linkObj) => {
        const tgtEeId = Number(
          typeof linkObj === "object" ? (linkObj.id ?? linkObj.ee_id) : linkObj
        );
        if (!tgtEeId) return;

        const tgtEe = mindMapProjectDetails.ee_details.find(
          (e) => Number(e.ee_id) === tgtEeId
        );
        if (!tgtEe) return;

        const netInterface =
          typeof linkObj === "object" ? linkObj.network_interface : null;
        const color =
          (typeof linkObj === "object" ? linkObj.color : null) || "#000000";

        if (isNetworkInterfaceMatched(netInterface)) {
          matchingEeIds.add(srcEeId);
          matchingEeIds.add(tgtEeId);

          const minId = Math.min(srcEeId, tgtEeId);
          const maxId = Math.max(srcEeId, tgtEeId);
          const linkKey = `${minId}---${maxId}---${(netInterface || "").trim().toLowerCase()}`;

          if (!seenEeLinks.has(linkKey)) {
            seenEeLinks.add(linkKey);
            matchingEeLinks.push({
              source: `ee-${srcEeId}`,
              target: `ee-${tgtEeId}`,
              color,
              network_interface: netInterface,
            });
          }
        }
      });
    });
  }

  const matchingSwmLinks = [];
  const seenSwmLinks = new Set();

  if (filterSwm && Array.isArray(mindMapProjectDetails.swm_details)) {
    mindMapProjectDetails.swm_details.forEach((swm) => {
      const srcSwmId = Number(swm.swm_id);
      if (!Array.isArray(swm.linked_swm_ids)) return;

      swm.linked_swm_ids.forEach((linkObj) => {
        const tgtSwmId = Number(
          typeof linkObj === "object" ? (linkObj.id ?? linkObj.swm_id) : linkObj
        );
        if (!tgtSwmId) return;

        const tgtSwm = mindMapProjectDetails.swm_details.find(
          (s) => Number(s.swm_id) === tgtSwmId
        );
        if (!tgtSwm) return;

        const netInterface =
          typeof linkObj === "object" ? linkObj.network_interface : null;
        const color =
          (typeof linkObj === "object" ? linkObj.color : null) || "#000000";

        if (isNetworkInterfaceMatched(netInterface)) {
          matchingSwmIds.add(srcSwmId);
          matchingSwmIds.add(tgtSwmId);

          const minId = Math.min(srcSwmId, tgtSwmId);
          const maxId = Math.max(srcSwmId, tgtSwmId);
          const linkKey = `${minId}---${maxId}---${(netInterface || "").trim().toLowerCase()}`;

          if (!seenSwmLinks.has(linkKey)) {
            seenSwmLinks.add(linkKey);
            matchingSwmLinks.push({
              source: `swm-${srcSwmId}`,
              target: `swm-${tgtSwmId}`,
              color,
              network_interface: netInterface,
            });
          }
        }
      });
    });

    matchingSwmIds.forEach((swmId) => {
      const swmObj = mindMapProjectDetails.swm_details.find(
        (s) => Number(s.swm_id) === swmId
      );
      if (swmObj?.ee_id != null) {
        parentEeIdsForSwms.add(Number(swmObj.ee_id));
      }
    });
  }

  const allParticipatingEeIds = new Set([
    ...matchingEeIds,
    ...parentEeIdsForSwms,
  ]);

  if (Array.isArray(mindMapProjectDetails.ee_details)) {
    allParticipatingEeIds.forEach((eeId) => {
      const eeObj = mindMapProjectDetails.ee_details.find(
        (e) => Number(e.ee_id) === eeId
      );
      if (eeObj) {
        const zoneNodeId = findZoneNodeIdForEe(eeObj, mindMapProjectDetails);
        if (zoneNodeId) {
          matchingZoneIds.add(zoneNodeId);
        }
      }
    });
  }

  return {
    isFilterActive: true,
    matchingEeIds,
    matchingSwmIds,
    parentEeIdsForSwms,
    matchingZoneIds,
    matchingEeLinks,
    matchingSwmLinks,
    filterEe,
    filterSwm,
  };
};

const computeFocusedFilter = ({
  selectedNodeId,
  filterMatches,
  mindMapProjectDetails,
}) => {
  if (!filterMatches?.isFilterActive || !selectedNodeId) return null;

  if (selectedNodeId.startsWith("ee-")) {
    const selectedEeNum = getEeId(selectedNodeId);
    const focusedEeLinks = (filterMatches.matchingEeLinks || []).filter(
      (link) =>
        getEeId(link.source) === selectedEeNum ||
        getEeId(link.target) === selectedEeNum,
    );
    const focusedTargetEeIds = new Set();
    focusedEeLinks.forEach((link) => {
      const srcId = getEeId(link.source);
      const tgtId = getEeId(link.target);
      const otherId = srcId === selectedEeNum ? tgtId : srcId;
      focusedTargetEeIds.add(otherId);
    });
    const visibleEeIds = new Set([selectedEeNum, ...focusedTargetEeIds]);

    return {
      type: "ee",
      visibleEeIds,
      visibleSwmIds: new Set(),
      activeLinks: focusedEeLinks,
    };
  }

  if (selectedNodeId.startsWith("swm-")) {
    const selectedSwmNum = getSwmId(selectedNodeId);
    const focusedSwmLinks = (filterMatches.matchingSwmLinks || []).filter(
      (link) =>
        getSwmId(link.source) === selectedSwmNum ||
        getSwmId(link.target) === selectedSwmNum,
    );
    const focusedTargetSwmIds = new Set();
    focusedSwmLinks.forEach((link) => {
      const srcId = getSwmId(link.source);
      const tgtId = getSwmId(link.target);
      const otherId = srcId === selectedSwmNum ? tgtId : srcId;
      focusedTargetSwmIds.add(otherId);
    });
    const visibleSwmIds = new Set([selectedSwmNum, ...focusedTargetSwmIds]);
    const visibleParentEeIds = new Set();

    visibleSwmIds.forEach((swmId) => {
      const swmObj = mindMapProjectDetails.swm_details?.find(
        (s) => Number(s.swm_id) === swmId,
      );
      if (swmObj?.ee_id != null) {
        visibleParentEeIds.add(Number(swmObj.ee_id));
      }
    });

    return {
      type: "swm",
      visibleEeIds: visibleParentEeIds,
      visibleSwmIds,
      activeLinks: focusedSwmLinks,
    };
  }

  return null;
};

function VlcZonalMindMap({
  selectedProjectId,
  selectedVariantId,
  isDialog = false,
  filters,
  onResetFilters,
}) {
  const dispatch = useDispatch();
  const lastRequestKeyRef = useRef("");
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [openDialog, setOpenDialog] = useState(false);

  const { projectDetailsMindMap } = useSelector(
    (state) => state.labCarReducer,
    shallowEqual,
  );
  const selectedVariantDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedVariantDetails,
  );

  const [mindMapProjectDetails, setMindMapProjectDetails] = useState({});

  const [flowNodes, setFlowNodes] = useState([]);
  const [flowEdges, setFlowEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [lastClickedZoneId, setLastClickedZoneId] = useState(null);
  const rfInstanceRef = useRef(null);

  const [hoverDialog, setHoverDialog] = useState({
    open: false,
    swmDetails: null,
    position: { x: 0, y: 0 },
    isLinkedSWM: false,
  });

  const [eeHoverDialog, setEeHoverDialog] = useState({
    open: false,
    eeDetails: null,
    position: { x: 0, y: 0 },
    isLinkedEE: false,
  });

  const filterMatches = useMemo(() => {
    return computeFilterMatches(filters, mindMapProjectDetails);
  }, [filters, mindMapProjectDetails]);

  const prevFilterActiveRef = useRef(false);

  useEffect(() => {
    if (filterMatches.isFilterActive) {
      const nextExpanded = new Set();
      filterMatches.matchingZoneIds.forEach((zid) => nextExpanded.add(zid));
      if (filterMatches.filterSwm) {
        filterMatches.parentEeIdsForSwms.forEach((eeId) => {
          nextExpanded.add(`ee-${eeId}`);
        });
      }
      setExpandedNodes(nextExpanded);
      setLastClickedZoneId("filter-applied");
    } else if (prevFilterActiveRef.current) {
      setExpandedNodes(new Set());
      setSelectedNode(null);
      setLastClickedZoneId("filter-cleared");
    }
    prevFilterActiveRef.current = filterMatches.isFilterActive;
  }, [filters, filterMatches]);

  useEffect(() => {
    if (selectedProjectId && selectedVariantId) {
      if (
        selectedVariantDetails?.project_id &&
        Number(selectedVariantDetails.project_id) !== Number(selectedProjectId)
      ) {
        setFlowNodes([]);
        setFlowEdges([]);
        setMindMapProjectDetails({});
        setExpandedNodes(new Set());
        setSelectedNode(null);
        return;
      }
      const requestKey = `${selectedProjectId}-${selectedVariantId}`;
      if (lastRequestKeyRef.current === requestKey) return;
      lastRequestKeyRef.current = requestKey;

      setExpandedNodes(new Set());
      setSelectedNode(null);
      setFlowNodes([]);
      setFlowEdges([]);
      setMindMapProjectDetails({});

      dispatch(
        HandleApiActions({
          ...labCar.get_project_details_mind_map,
          params: {
            project_id: selectedProjectId,
            variant_id: selectedVariantId,
          },
          show_toast: false,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedProjectId,
    selectedVariantId,
    selectedVariantDetails?.project_id,
    dispatch,
  ]);

  useEffect(() => {
    if (projectDetailsMindMap?.zones) {
      if (
        selectedProjectId &&
        Number(projectDetailsMindMap?.project_id) !== Number(selectedProjectId)
      ) {
        return;
      }
      setMindMapProjectDetails(projectDetailsMindMap);
      setExpandedNodes(new Set());
      setSelectedNode(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDetailsMindMap, selectedProjectId]);

  const handlePaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const handleReset = useCallback(() => {
    setExpandedNodes(new Set());
    setSelectedNode(null);
    setLastClickedZoneId(null);
    setHoverDialog({
      open: false,
      swmDetails: null,
      position: { x: 0, y: 0 },
      isLinkedSWM: false,
    });
    setEeHoverDialog({
      open: false,
      eeDetails: null,
      position: { x: 0, y: 0 },
      isLinkedEE: false,
    });
    onResetFilters?.();
    setTimeout(() => {
      rfInstanceRef.current?.fitView({ padding: 0.1, duration: 300 });
    }, 50);
  }, [onResetFilters]);

  const handleToggleEe = useCallback((eeId) => {
    setSelectedNode((prev) => (prev?.startsWith("ee-") ? null : prev));
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      const willOpen = !next.has(eeId);
      if (willOpen) {
        next.add(eeId);
        setLastClickedZoneId(eeId);
      } else {
        next.delete(eeId);
      }
      return next;
    });
  }, []);

  const createNodesAndEdges = useCallback(
    (hierarchyData, selectedNodeId, expandedNodes, filterMatches) => {
      const focusedFilter = computeFocusedFilter({
        selectedNodeId,
        filterMatches,
        mindMapProjectDetails,
      });

      const projectId = hierarchyData.id;
      const projectNode = {
        id: projectId,
        type: "projectNode",
        data: {
          name: hierarchyData.name,
        },
        position: { x: 0, y: 0 },
        style: {
          width: nodeWidth,
          height: nodeHeight,
        },
      };

      const zones = hierarchyData.children || [];
      const offsets = calculateZoneOverlapOffsets({
        zones,
        expandedNodes,
        nameToPos: ZONE_NAME_TO_POS,
      });

      const {
        nodes: zoneNodes,
        edges: zoneEdges,
        zonePositionsMap,
      } = generateZoneNodesAndEdges(zones, projectId, offsets);

      const nodes = [projectNode, ...zoneNodes];
      const edges = [...zoneEdges];

      const { swmLinkedMap, swmParentEeMap } = buildSwmMappings(zones);

      for (const zone of zones) {
        if (!expandedNodes.has(zone.id)) continue;
        const zonePos = zonePositionsMap.get(zone.id) || { x: 0, y: 0 };
        generateEeNodesAndEdgesForZone({
          zone,
          zonePos,
          expandedNodes,
          selectedNodeId,
          mindMapProjectDetails,
          swmLinkedMap,
          swmParentEeMap,
          nodes,
          edges,
          onToggleEe: handleToggleEe,
          filterMatches,
          focusedFilter,
        });
      }

      if (filterMatches?.isFilterActive) {
        const activeEeLinks = focusedFilter
          ? (focusedFilter.type === "ee" ? focusedFilter.activeLinks : [])
          : (filterMatches.matchingEeLinks || []);

        const activeSwmLinks = focusedFilter
          ? (focusedFilter.type === "swm" ? focusedFilter.activeLinks : [])
          : (filterMatches.matchingSwmLinks || []);

        const totalFilterLinks = activeEeLinks.length + activeSwmLinks.length;

        activeEeLinks.forEach((link, idx) => {
          edges.push({
            id: `ee-filter-link-${link.source}-${link.target}-${link.network_interface}`,
            source: link.source,
            target: link.target,
            type: "swmLinkEdge",
            animated: true,
            zIndex: 1000 + idx,
            data: {
              linkIndex: idx,
              totalLinks: totalFilterLinks,
              color: link.color || "#000000",
            },
            style: {
              stroke: link.color || "#000000",
              strokeWidth: 3.5,
            },
          });
        });

        activeSwmLinks.forEach((link, idx) => {
          const linkIndex = activeEeLinks.length + idx;
          edges.push({
            id: `swm-filter-link-${link.source}-${link.target}-${link.network_interface}`,
            source: link.source,
            target: link.target,
            type: "swmLinkEdge",
            animated: true,
            zIndex: 2000 + idx,
            data: {
              linkIndex,
              totalLinks: totalFilterLinks,
              color: link.color || "#000000",
            },
            style: {
              stroke: link.color || "#000000",
              strokeWidth: 3.5,
            },
          });
        });
      } else {
        // If a software module is selected, add link edges to all its linked software modules
        if (
          selectedNodeId?.startsWith("swm-") &&
          Array.isArray(mindMapProjectDetails?.swm_details)
        ) {
          const selSwmNum = Number(selectedNodeId.replaceAll("swm-", ""));
          const selSwm = mindMapProjectDetails.swm_details.find(
            (s) => Number(s.swm_id) === selSwmNum,
          );

          if (Array.isArray(selSwm?.linked_swm_ids)) {
            selSwm.linked_swm_ids.forEach((linkedObj, idx) => {
              const targetSwmId = `swm-${linkedObj.id}`;
              const targetColor = linkedObj.color || "#000000";

              edges.push({
                id: `swm-link-${selectedNodeId}-${targetSwmId}`,
                source: selectedNodeId,
                target: targetSwmId,
                type: "swmLinkEdge",
                animated: true,
                zIndex: 1000 + idx,
                data: {
                  linkIndex: idx,
                  totalLinks: selSwm.linked_swm_ids.length,
                  color: targetColor,
                },
                style: {
                  stroke: targetColor,
                  strokeWidth: 3.5,
                },
              });
            });
          }
        }

        // If an EE component is selected, add link edges to all its linked EE components
        if (
          selectedNodeId?.startsWith("ee-") &&
          Array.isArray(mindMapProjectDetails?.ee_details)
        ) {
          const selEeNum = Number(selectedNodeId.replaceAll("ee-", ""));
          const selEe = mindMapProjectDetails.ee_details.find(
            (ee) => Number(ee.ee_id) === selEeNum,
          );

          if (Array.isArray(selEe?.linked_ee_ids)) {
            selEe.linked_ee_ids.forEach((linkedObj, idx) => {
              const targetIdNum =
                typeof linkedObj === "object"
                  ? (linkedObj.id ?? linkedObj.ee_id)
                  : linkedObj;
              const targetEeId = `ee-${targetIdNum}`;
              const targetColor =
                (typeof linkedObj === "object" ? linkedObj.color : null) ||
                "#000000";

              edges.push({
                id: `ee-link-${selectedNodeId}-${targetEeId}`,
                source: selectedNodeId,
                target: targetEeId,
                type: "swmLinkEdge",
                animated: true,
                zIndex: 1000 + idx,
                data: {
                  linkIndex: idx,
                  totalLinks: selEe.linked_ee_ids.length,
                  color: targetColor,
                },
                style: {
                  stroke: targetColor,
                  strokeWidth: 3.5,
                },
              });
            });
          }
        }
      }

      return { nodes, edges };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      mindMapProjectDetails,
      handleToggleEe,
    ],
  );

  useEffect(() => {
    if (mindMapProjectDetails?.zones) {
      const hierarchyData = createHierarchyData(mindMapProjectDetails);
      const { nodes, edges } = createNodesAndEdges(
        hierarchyData,
        selectedNode,
        expandedNodes,
        filterMatches,
      );

      setFlowNodes(nodes);
      setFlowEdges(edges);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mindMapProjectDetails, selectedNode, expandedNodes, createNodesAndEdges, filterMatches]);

  useEffect(() => {
    if (!lastClickedZoneId) return;
    const t = setTimeout(() => {
      if (
        rfInstanceRef?.current?.fitView
      ) {
        rfInstanceRef.current.fitView({ padding: 0.1 });
      }
      setLastClickedZoneId(null);
    }, 140); // slight delay to let DOM update
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastClickedZoneId]);

  const onNodesChange = useCallback(
    (changes) => setFlowNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  );

  const onEdgesChange = useCallback(
    (changes) => setFlowEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  );

  const onConnect = useCallback(
    (params) => setFlowEdges((eds) => addEdge(params, eds)),
    [],
  );

  const handleNodeMouseEnter = useCallback(
    (event, node) => {
      const mousePos = getMousePosition(event);

      if (node.id.startsWith("swm-")) {
        handleSwmHover({
          node,
          mousePos,
          mindMapProjectDetails,
          selectedNode,
          setHoverDialog,
          filterMatches,
        });
        return;
      }

      if (node.id.startsWith("ee-")) {
        handleEeHover({
          node,
          mousePos,
          mindMapProjectDetails,
          selectedNode,
          setEeHoverDialog,
          filterMatches,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      mindMapProjectDetails.swm_details,
      mindMapProjectDetails.ee_details,
      selectedNode,
      filterMatches,
    ],
  );
  const handleNodeMouseLeave = useCallback((event, node) => {
    if (node.id.startsWith("swm-")) {
      setHoverDialog({
        open: false,
        swmDetails: null,
        position: { x: 0, y: 0 },
        isLinkedSWM: false,
      });
    } else if (node.id.startsWith("ee-")) {
      setEeHoverDialog({
        open: false,
        eeDetails: null,
        position: { x: 0, y: 0 },
        isLinkedEE: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNodeClick = useCallback(
    (event, node) => {
      if (isZoneNode(node.id)) {
        setSelectedNode(node.id);
        handleZoneOrEeClick({
          node,
          expandedNodes,
          setExpandedNodes,
          setLastClickedZoneId,
        });
        return;
      }

      if (filterMatches?.isFilterActive) {
        setSelectedNode((prev) => (prev === node.id ? null : node.id));
        return;
      }

      setSelectedNode(node.id);

      if (isEeNode(node.id)) {
        handleEeClick({
          node,
          expandedNodes,
          mindMapProjectDetails,
          setExpandedNodes,
          setLastClickedZoneId,
        });
        return;
      }

      if (isSwmNode(node.id)) {
        handleSwmClick({
          node,
          expandedNodes,
          mindMapProjectDetails,
          setExpandedNodes,
        });
      }
    },
    [expandedNodes, mindMapProjectDetails, filterMatches],
  );
  const animatedEdges = useMemo(() => {
    return flowEdges.map((edge) => {
      if (edge.type === "swmLinkEdge") {
        return edge;
      }
      if (edge.source === selectedNode || edge.target === selectedNode) {
        return { ...edge, animated: true };
      }
      return { ...edge, animated: false };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowEdges, selectedNode]);

  if (!isDialog) {
    return (
      <>
        <Paper
          elevation={2}
          sx={{
            p: 3,
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              elevation: 6,
              transform: "translateY(-2px)",
              bgcolor: "#f8f9fa",
            },
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            textAlign: "center",
            mt: 2,
          }}
          onClick={() => setOpenDialog(true)}
        >
          <Box sx={{ py: 2 }}>
            <Typography
              variant="h5"
              gutterBottom
              color="primary"
              sx={{ fontWeight: "bold" }}
            >
              🗺️ Zonal Mind Map
            </Typography>
            <Typography variant="h6" color="text.primary" gutterBottom>
              {mindMapProjectDetails?.name || "Project"} Architecture
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 2, mb: 3 }}
            >
              Explore the interactive visualization of your project's
              architecture, zones, EE components, and software modules
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: "italic" }}>
              Click to open interactive mind map in full screen
            </Typography>
          </Box>
        </Paper>

        <ZonalMindMapDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          selectedProjectId={selectedProjectId}
          selectedVariantId={selectedVariantId}
          projectName={mindMapProjectDetails?.name}
        />
      </>
    );
  }

  return (
    <Box sx={isDialog ? dialogStyles.mainContainer : styles.mainContainer}>
      <ReactFlow
        key={`zonal-rf-${selectedProjectId || "na"}-${selectedVariantId || "na"}`}
        nodes={flowNodes}
        edges={animatedEdges}
        nodeTypes={NODE_TYPES}
        edgeTypes={EDGE_TYPES}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        onNodeMouseEnter={handleNodeMouseEnter}
        onNodeMouseLeave={handleNodeMouseLeave}
        onInit={(instance) => (rfInstanceRef.current = instance)}
        fitView
        minZoom={0.1}
        maxZoom={4}
        defaultZoom={isDialog ? 0.3 : 0.5}
      >
        <Background />
        <Controls>
          <ControlButton onClick={handleReset} title="Reset" aria-label="Reset">
            <RestartAltIcon sx={{ fontSize: "14px" }} />
          </ControlButton>
        </Controls>
      </ReactFlow>

      <SWMHoverTooltip
        open={hoverDialog.open}
        swmDetails={hoverDialog.swmDetails}
        position={hoverDialog.position}
        isLinkedSWM={hoverDialog.isLinkedSWM}
      />

      <EEHoverTooltip
        open={eeHoverDialog.open}
        eeDetails={eeHoverDialog.eeDetails}
        position={eeHoverDialog.position}
        isLinkedEE={eeHoverDialog.isLinkedEE}
      />
    </Box>
  );
}

export default VlcZonalMindMap;
export { ZonalMindMapDialog };

const styles = {
  mainContainer: {
    height: "520px",
    width: "100%",
    backgroundColor: "#F5F5F5",
    border: "1px solid grey",
    borderRadius: 1,
    padding: "20px",
    mt: 1,
    boxShadow: 4,
  },
};

const dialogStyles = {
  mainContainer: {
    height: "calc(90vh - 100px)", // Adjust for dialog header
    width: "100%",
    backgroundColor: "#F5F5F5",
    border: "none", // Remove border since it's in dialog
    borderRadius: 0, // Remove border radius
    padding: "10px", // Reduce padding
    mt: 0, // Remove margin top
    boxShadow: "none", // Remove box shadow
  },
};


SWMHoverTooltip.propTypes = {
  open: PropTypes.bool,
  swmDetails: PropTypes.shape({
    name: PropTypes.string,
    version: PropTypes.string,
    description: PropTypes.string,
    network_interface: PropTypes.shape({
      interface_name: PropTypes.string,
      color: PropTypes.string,
    }),
  }),
  position: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
  isLinkedSWM: PropTypes.bool,
};

EEHoverTooltip.propTypes = {
  open: PropTypes.bool,
  eeDetails: PropTypes.shape({
    name: PropTypes.string,
    assembly_number: PropTypes.string,
    version: PropTypes.string,
    description: PropTypes.string,
    network_interface: PropTypes.shape({
      interface_name: PropTypes.string,
      color: PropTypes.string,
    }),
  }),
  position: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
  isLinkedEE: PropTypes.bool,
};

ProjectNode.propTypes = {
  data: PropTypes.shape({ name: PropTypes.string }),
};

ZoneNode.propTypes = {
  data: PropTypes.shape({
    labelText: PropTypes.string,
    rotate: PropTypes.bool,
    isFromFrontSide: PropTypes.bool,
    isRear: PropTypes.bool,
  }),
};

SWMNode.propTypes = {
  data: PropTypes.shape({
    name: PropTypes.string,
    isRearZone: PropTypes.bool,
    isFrontZone: PropTypes.bool,
    isCentralLeftZone: PropTypes.bool,
    isRearLeftZone: PropTypes.bool,
    isRearRightZone: PropTypes.bool,
    background: PropTypes.string,
  }),
};

// Also ZonalMindMap might be VlcZonalMindMap
// Let's just use ZonalMindMap, VlcZonalMindMap, ZonalMindMapDialog
VlcZonalMindMap.propTypes = {
  selectedProjectId: PropTypes.any,
  selectedVariantId: PropTypes.any,
  isDialog: PropTypes.bool,
};
ZonalMindMapDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  selectedProjectId: PropTypes.any,
  selectedVariantId: PropTypes.any,
  projectName: PropTypes.string,
};
